import React, { useEffect, useState } from 'react'
import { CREDIT_PRICES, addUsageCredits } from '../usage'
 
const PROCS = {
  identifyDups: { id: 103, name: 'AP_IDENTIFY_DUPLICATES' },
  updateMerges: { id: 104, name: 'kognative_LP_UPDATE_POSSIBLE_MERGES' },
  mergeCustomer: { id: 105, name: 'kognative_MERGE_CUSTOMER' },
  mergeCustomerApi: { name: 'kognative_MERGE_CUSTOMER_API' },
  managePair: { id: 107, name: 'kognative_MANAGE_MERGE_PAIR' }
}
 
const TESSITURA_BASE = 'https://spoletussc0webprod.tnhs.cloud/Tessitura/#/crm/constituents'
const RECENT_MERGE_WINDOW_MINUTES = 5

const buildCustomExecuteBody = (proc, parameterValues = []) => ({
  ...(proc.id && proc.name !== 'kognative_MERGE_CUSTOMER' ? { ProcedureId: proc.id } : {}),
  ProcedureName: proc.name,
  ...(parameterValues.length ? { ParameterValues: parameterValues } : {})
})

const getConstituentName = (data, fallbackId) => {
  const direct = data?.SortName ?? data?.sortName ?? data?.DisplayName ?? data?.displayName ?? data?.Name ?? data?.name
  if (direct) return String(direct)
  const first = data?.FirstName ?? data?.firstName ?? data?.FName ?? data?.fname
  const last = data?.LastName ?? data?.lastName ?? data?.LName ?? data?.lname
  const full = [first, last].filter(Boolean).join(' ').trim()
  return full || `#${fallbackId}`
}

function LoadingStatValue() {
  return <span style={styles.loadingStatValue} aria-label="Loading stat" />
}

function LoadingSourceList() {
  return (
    <div style={styles.sourceList} aria-label="Loading duplicate sources">
      {[0, 1, 2].map((row) => (
        <div key={row} style={styles.sourceRow}>
          <span style={styles.loadingSourceDot} />
          <span style={styles.loadingSourceLine} />
        </div>
      ))}
    </div>
  )
}
 
function ConstituentMerge({ orgData, onUse, onUsageUpdated }) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 640 : false)
  const [activeTab, setActiveTab] = useState('bulk')
  const [pool, setPool] = useState([])
  const [unscheduledCriteria, setUnscheduledCriteria] = useState(new Set())
  const [mergedToday, setMergedToday] = useState([])
  const [loading, setLoading] = useState(false)
  const [swappingCriterion, setSwappingCriterion] = useState(null)
  const [cancellingCriterion, setCancellingCriterion] = useState(null)
  const [blockingCustomer, setBlockingCustomer] = useState(null)
  const [step, setStep] = useState(null)
  const [message, setMessage] = useState(null)
  const [confirmCommit, setConfirmCommit] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [householdResult, setHouseholdResult] = useState(null)
  const [convertingCriterion, setConvertingCriterion] = useState(null)
  const [mergeKeepNo, setMergeKeepNo] = useState('')
  const [mergeDeleteNo, setMergeDeleteNo] = useState('')
  const [recentMerges, setRecentMerges] = useState([])
  const [recentLoading, setRecentLoading] = useState(false)
  const [toolsLoading, setToolsLoading] = useState(null)   // 'merge' | 'separate' | 'unmerge' | null
  const [mergeToolResult, setMergeToolResult] = useState(null)
  const [optimizeResult, setOptimizeResult] = useState(null)
  const [unmergeKeepNo, setUnmergeKeepNo] = useState('')
  const [unmergeNo, setUnmergeNo] = useState('')
  const [unmergeToolResult, setUnmergeToolResult] = useState(null)
  const [unmergeConfirm, setUnmergeConfirm] = useState(null)   // row being confirmed
  const [unmergeNotice, setUnmergeNotice] = useState(null)
  const [unmergingRow, setUnmergingRow] = useState(null)        // customer_no being processed
  const [unmergedRows, setUnmergedRows] = useState(new Set())   // customer_nos already unmerged
  const [mergeStats, setMergeStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [hoveredFailureKey, setHoveredFailureKey] = useState(null)
  const [oneOffConfirm, setOneOffConfirm] = useState(null)
 
  const headers = {
    'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
    'x-tessitura-url': orgData.organizations.tessitura_base_url,
    'Content-Type': 'application/json'
  }
 
  const callProc = async (proc, parameterValues = []) => {
    const body = buildCustomExecuteBody(proc, parameterValues)
 
    const execute = async (payload) => {
      const response = await fetch(`/api/tessitura?endpoint=Custom/Execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      })
      const text = await response.text()
      return { ok: response.ok, status: response.status, text }
    }

    return execute(body)
  }

  const callTessitura = async (endpoint, method = 'GET', body = null) => {
    const response = await fetch(`/api/tessitura?endpoint=${encodeURIComponent(endpoint)}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  const fetchConstituentPreview = async (customerNo) => {
    const id = String(customerNo || '').trim()
    const { ok, status, text } = await callTessitura(`CRM/Constituents/${encodeURIComponent(id)}`)
    if (!ok) {
      throw new Error(getErrorMessage(`Could not load customer #${id}.`, status, text))
    }
    let data = {}
    try {
      data = JSON.parse(text)
    } catch {
      throw new Error(`Could not read customer #${id} from Tessitura.`)
    }
    return { customerNo: id, name: getConstituentName(data, id) }
  }

  const getErrorMessage = (fallback, status, text) => {
    const statusText = status ? ` Tessitura returned HTTP ${status}.` : ''
    const noDetails = `${fallback}${statusText} No error details were returned.`
    if (!text) return noDetails
    try {
      const parsed = JSON.parse(text)
      const tessituraDescription = Array.isArray(parsed)
        ? parsed.find(item => typeof item?.Description === 'string')?.Description
        : parsed?.Description
      const primary = parsed?.Message || parsed?.message || parsed?.error
      const retryText = parsed?.retryAttempt?.text?.Message || parsed?.retryAttempt?.text?.message || parsed?.retryAttempt?.text?.error || parsed?.retryAttempt?.text
      const firstText = parsed?.firstAttempt?.text?.Message || parsed?.firstAttempt?.text?.message || parsed?.firstAttempt?.text?.error || parsed?.firstAttempt?.text
      const upstreamText = parsed?.upstreamText || parsed?.text
      const details = [tessituraDescription, retryText, firstText, upstreamText, primary]
        .find(detail => typeof detail === 'string' && detail.trim())
      if (details) return details.length > 280 ? `${details.slice(0, 280)}...` : details
      if (parsed?.requestBody?.ProcedureName) {
        return `${fallback}${statusText} Tessitura rejected ${parsed.requestBody.ProcedureName}, but did not return details.`
      }
      return noDetails
    } catch {
      return text.length > 280 ? `${text.slice(0, 280)}...` : text
    }
  }

  const recordMergeUsage = async (mergeCount) => {
    const credits = Number(mergeCount || 0) * CREDIT_PRICES.merge
    if (credits <= 0) return
    try {
      await addUsageCredits(orgData, credits)
      if (onUsageUpdated) await onUsageUpdated()
    } catch (err) {
      console.error('Usage update failed:', err)
    }
  }

  const fetchPool = async () => {
    const { ok, text } = await callProc(PROCS.managePair, [
      { Name: '@mode', Value: 'get_pool' }
    ])
    if (ok) {
      try {
        const data = JSON.parse(text)
        setPool(Array.isArray(data) ? data : [])
      } catch {
        setPool([])
      }
    }
  }

  const fetchRecentMerges = async () => {
    setRecentLoading(true)
    const { ok, text } = await callProc(PROCS.managePair, [
      { Name: '@mode', Value: 'merged_today' }
    ])
    if (ok) {
      try {
        const data = JSON.parse(text)
        setRecentMerges(Array.isArray(data) ? data : [])
      } catch {
        setRecentMerges([])
      }
    }
    setRecentLoading(false)
  }

  const fetchMergeStats = async () => {
    setStatsLoading(true)
    const { ok, text } = await callProc(PROCS.managePair, [
      { Name: '@mode', Value: 'merge_stats' }
    ])
    if (ok) {
      try {
        const rows = JSON.parse(text)
        const list = Array.isArray(rows) ? rows : []
        const first = list[0] || {}
        setMergeStats({
          activePairs: Number(first.active_pairs || 0),
          needsReviewPairs: Number(first.needs_review_pairs || 0),
          queuedPairs: Number(first.queued_pairs || 0),
          queuedRecords: Number(first.queued_records || 0),
          mergeAttempts: Number(first.merge_attempts || 0),
          successfulMerges: Number(first.successful_merges || 0),
          failedMerges: Number(first.failed_merges || 0),
          unmerged: Number(first.unmerged || 0),
          lastMergeDt: first.last_merge_dt,
          sources: list
            .filter(row => row.source_created_by)
            .map(row => ({
              name: row.source_created_by,
              count: Number(row.source_merge_count || 0),
              successful: Number(row.source_successful_merges || 0)
            }))
        })
      } catch {
        setMergeStats(null)
      }
    }
    setStatsLoading(false)
  }

  useEffect(() => {
    fetchMergeStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
 
  const handleFindAndPromote = async () => {
    setLoading(true)
    setStep('finding')
    setMessage(null)
    setPool([])
    setUnscheduledCriteria(new Set())
    setMergedToday([])
 
    try {
      const { ok: ok1 } = await callProc(PROCS.identifyDups, [
        { Name: '@changed_since_days', Value: '1095' },
        { Name: '@identify_method1', Value: '1' },
        { Name: '@identify_method2', Value: '2' },
        { Name: '@identify_method3', Value: '3' },
        { Name: '@include_inactive', Value: 'N' },
        { Name: '@list_no', Value: null }
      ])
 
      if (!ok1) {
        setMessage({ type: 'error', text: 'Failed to identify duplicates.' })
        setLoading(false)
        return
      }
 
      const { ok: ok2 } = await callProc(PROCS.updateMerges)
 
      if (!ok2) {
        setMessage({ type: 'error', text: 'Failed to promote candidates.' })
        setLoading(false)
        return
      }
 
      await fetchPool()
      await fetchMergeStats()
      setStep('review')
      setMessage({ type: 'success', text: 'High confidence duplicates are ready to review. Needs Review suggestions can be promoted manually.' })
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }
 
    setLoading(false)
  }
 
  const handleCommit = async () => {
    setLoading(true)
    setConfirmCommit(false)
    setStep('committing')
    setMessage(null)
    try {
      const pairs = pool.filter(row => String(row.status || '').toUpperCase() === 'D')
      const responses = []
      for (const pair of pairs) {
        const response = await callProc(PROCS.mergeCustomerApi, [
          { Name: '@kept_id_filter', Value: String(pair.keep_cust) },
          { Name: '@delete_id_filter', Value: String(pair.customer_no) }
        ])
        responses.push({ pair, ...response })
        if (!response.ok) {
          await callProc(PROCS.managePair, [
            { Name: '@customer_no_1', Value: String(pair.keep_cust) },
            { Name: '@customer_no_2', Value: String(pair.customer_no) },
            { Name: '@mode', Value: 'api_merge_failed' },
            { Name: '@failure_reason', Value: getErrorMessage('Tessitura API merge request failed.', response.status, response.text) }
          ])
        }
      }
      console.log('merge responses:', responses)
      {
        let recentRows = []
        const { ok: ok2, text } = await callProc(PROCS.managePair, [
          { Name: '@mode', Value: 'merged_recent' }
        ])
        if (ok2) {
          try {
            const data = JSON.parse(text)
            recentRows = Array.isArray(data) ? data : []
            setMergedToday(recentRows)
          } catch {
            setMergedToday([])
          }
        }
        const currentRows = recentRows.filter(row => pairs.some(pair =>
          String(row.customer_no) === String(pair.customer_no) &&
          String(row.keep_cust) === String(pair.keep_cust)
        ))
        const failedRows = currentRows.filter(row => String(row.status || '').toUpperCase() === 'A')
        const successfulRows = currentRows.filter(row => String(row.status || '').toUpperCase() === 'M')
        const unrecordedFailures = responses.filter(response =>
          !response.ok && !currentRows.some(row =>
            String(row.customer_no) === String(response.pair.customer_no) &&
            String(row.keep_cust) === String(response.pair.keep_cust)
          )
        )
        const failedCount = failedRows.length + unrecordedFailures.length
        setPool([])
        setStep('done')
        setMessage({
          type: failedCount > 0 ? 'error' : 'success',
          text: failedCount > 0
            ? `Merges completed with ${failedCount} failed ${failedCount === 1 ? 'pair' : 'pairs'}. Successful pairs were committed.`
            : 'Merges committed successfully.'
        })
        await fetchMergeStats()
        await recordMergeUsage(successfulRows.length)
        if (onUse) onUse(3)
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + err.message })
    }
 
    setLoading(false)
  }
 
  const handleSwap = async (criterion, keepRow, deleteRow) => {
    setSwappingCriterion(criterion)
 
    setPool(prev => prev.map(r => {
      if (r.customer_no === keepRow.customer_no) return { ...r, status: 'D' }
      if (r.customer_no === deleteRow.customer_no) return { ...r, status: 'K' }
      return r
    }))
 
    try {
      const { ok } = await callProc(PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(deleteRow.customer_no) },
        { Name: '@customer_no_2', Value: String(keepRow.customer_no) },
        { Name: '@mode', Value: 'swap' }
      ])
      if (!ok) {
        setPool(prev => prev.map(r => {
          if (r.customer_no === keepRow.customer_no) return { ...r, status: 'K' }
          if (r.customer_no === deleteRow.customer_no) return { ...r, status: 'D' }
          return r
        }))
        setMessage({ type: 'error', text: 'Failed to swap pair. Please try again.' })
      }
    } catch (err) {
      setPool(prev => prev.map(r => {
        if (r.customer_no === keepRow.customer_no) return { ...r, status: 'K' }
        if (r.customer_no === deleteRow.customer_no) return { ...r, status: 'D' }
        return r
      }))
      setMessage({ type: 'error', text: 'Error swapping pair: ' + err.message })
    }
 
    setSwappingCriterion(null)
  }
 
  const handleCancel = async (criterion, keepRow, deleteRow) => {
    setCancellingCriterion(criterion)
 
    try {
      const { ok } = await callProc(PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(keepRow.customer_no) },
        { Name: '@customer_no_2', Value: String(deleteRow.customer_no) },
        { Name: '@mode', Value: 'unschedule' }
      ])
      if (ok) {
        setPool(prev => prev.map(r =>
          r.criterion === criterion ? { ...r, status: 'P', keep_cust: null } : r
        ))
        setUnscheduledCriteria(prev => new Set([...prev, criterion]))
        await fetchMergeStats()
      } else {
        setMessage({ type: 'error', text: 'Failed to cancel pair. Please try again.' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error cancelling pair: ' + err.message })
    }
 
    setCancellingCriterion(null)
  }
 
  const handleSchedule = async (criterion, row1, row2) => {
    setCancellingCriterion(criterion)
 
    try {
      const { ok } = await callProc(PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(row1.customer_no) },
        { Name: '@customer_no_2', Value: String(row2.customer_no) },
        { Name: '@mode', Value: 'schedule' }
      ])
      if (ok) {
        setPool(prev => prev.map(r => {
          if (r.customer_no === row1.customer_no) return { ...r, status: 'K', keep_cust: row1.customer_no }
          if (r.customer_no === row2.customer_no) return { ...r, status: 'D', keep_cust: row1.customer_no }
          return r
        }))
        setUnscheduledCriteria(prev => {
          const next = new Set(prev)
          next.delete(criterion)
          return next
        })
        await fetchMergeStats()
      } else {
        setMessage({ type: 'error', text: 'Failed to schedule pair. Please try again.' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error scheduling pair: ' + err.message })
    }
 
    setCancellingCriterion(null)
  }

  const handleBlockMerge = async (row) => {
    setBlockingCustomer(row.customer_no)
    setMessage(null)

    try {
      const existing = await callTessitura(`CRM/Attributes?constituentId=${encodeURIComponent(row.customer_no)}&keywordIds=16`)
      if (existing.ok) {
        let alreadyBlocked = false
        try {
          const attributes = JSON.parse(existing.text)
          alreadyBlocked = Array.isArray(attributes) && attributes.some(attribute =>
            Number(attribute?.Keyword?.Id) === 16 &&
            String(attribute?.Value || '').toLowerCase() === 'yes'
          )
        } catch { /* continue to create if the lookup response is not parseable */ }

        if (!alreadyBlocked) {
          const created = await callTessitura('CRM/Attributes', 'POST', {
            Keyword: { Id: 16 },
            Constituent: { Id: Number(row.customer_no) },
            Value: 'Yes'
          })
          if (!created.ok) {
            setMessage({ type: 'error', text: getErrorMessage('Failed to block merges for this customer.', created.status, created.text) })
            setBlockingCustomer(null)
            return
          }
        }
      } else {
        const created = await callTessitura('CRM/Attributes', 'POST', {
          Keyword: { Id: 16 },
          Constituent: { Id: Number(row.customer_no) },
          Value: 'Yes'
        })
        if (!created.ok) {
          setMessage({ type: 'error', text: getErrorMessage('Failed to block merges for this customer.', created.status, created.text) })
          setBlockingCustomer(null)
          return
        }
      }

      setPool(prev => prev.filter(r => r.criterion !== row.criterion))
      setMessage({ type: 'success', text: `Block Merges attribute applied to customer #${row.customer_no}.` })
      await fetchMergeStats()
    } catch (err) {
      setMessage({ type: 'error', text: 'Error blocking merges: ' + err.message })
    }

    setBlockingCustomer(null)
  }

  const handleConvertToHousehold = async (criterion, keepRow, deleteRow) => {
    setConvertingCriterion(criterion)
    try {
      const { ok, text } = await callProc(PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(keepRow.customer_no) },
        { Name: '@customer_no_2', Value: String(deleteRow.customer_no) },
        { Name: '@mode', Value: 'convert_to_household' }
      ])
      if (ok) {
        let hhNo = null
        try {
          const data = JSON.parse(text)
          hhNo = Array.isArray(data) ? data[0]?.household_customer_no : data?.household_customer_no
        } catch { /* */ }
        setPool(prev => prev.filter(r => r.criterion !== criterion))
        setHouseholdResult({
          householdNo: hhNo,
          name: `${keepRow.lname} Household`,
          a1: keepRow,
          a2: deleteRow
        })
      } else {
        setMessage({ type: 'error', text: 'Failed to convert to household.' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error converting: ' + err.message })
    }
    setConvertingCriterion(null)
  }

  const handleOneOffMerge = async () => {
    if (!mergeKeepNo || !mergeDeleteNo) return
    setToolsLoading('merge')
    setMergeToolResult(null)
    setOneOffConfirm(null)

    try {
      const [keep, deleteCustomer] = await Promise.all([
        fetchConstituentPreview(mergeKeepNo),
        fetchConstituentPreview(mergeDeleteNo)
      ])
      setOneOffConfirm({ keep, deleteCustomer })
    } catch (err) {
      setMergeToolResult({
        type: 'error',
        text: err.message
      })
    }

    setToolsLoading(null)
  }

  const commitOneOffMerge = async () => {
    if (!oneOffConfirm) return
    const keepNo = oneOffConfirm.keep.customerNo
    const deleteNo = oneOffConfirm.deleteCustomer.customerNo
    setToolsLoading('merge')
    setMergeToolResult(null)
    const scheduleResponse = await callProc(PROCS.managePair, [
      { Name: '@customer_no_1', Value: String(keepNo) },
      { Name: '@customer_no_2', Value: String(deleteNo) },
      { Name: '@mode', Value: 'one_off_schedule' }
    ])
    if (!scheduleResponse.ok) {
      setMergeToolResult({
        type: 'error',
        text: getErrorMessage('Failed to stage merge pair.', scheduleResponse.status, scheduleResponse.text)
      })
      setToolsLoading(null)
      return
    }

    const { ok, status, text } = await callProc(PROCS.mergeCustomerApi, [
      { Name: '@kept_id_filter', Value: String(keepNo) },
      { Name: '@delete_id_filter', Value: String(deleteNo) }
    ])
    if (!ok) {
      await callProc(PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(keepNo) },
        { Name: '@customer_no_2', Value: String(deleteNo) },
        { Name: '@mode', Value: 'api_merge_failed' },
        { Name: '@failure_reason', Value: getErrorMessage('Tessitura API merge request failed.', status, text) }
      ])
      const { ok: recentOk, text: recentText } = await callProc(PROCS.managePair, [
        { Name: '@mode', Value: 'merged_recent' }
      ])
      let verifiedRows = []
      if (recentOk) {
        try {
          const parsed = JSON.parse(recentText)
          verifiedRows = Array.isArray(parsed) ? parsed : []
        } catch {
          verifiedRows = []
        }
      }
      const verified = status === 400 && verifiedRows.some(row => String(row.customer_no) === String(deleteNo) || String(row.keep_cust) === String(keepNo))
      if (!verified) {
        setMergeToolResult({
          type: 'error',
          text: getErrorMessage('Failed to merge.', status, text)
        })
        setToolsLoading(null)
        return
      }
    }
    await recordMergeUsage(1)
    setMergeToolResult({
      type: 'success',
      keepNo, keepName: oneOffConfirm.keep.name,
      deleteNo, deleteName: oneOffConfirm.deleteCustomer.name,
      mergedAt: new Date().toLocaleTimeString()
    })
    setOneOffConfirm(null)
    setMergeKeepNo('')
    setMergeDeleteNo('')
    await fetchMergeStats()
    await fetchRecentMerges()
    setToolsLoading(null)
  }

  const handleUnmergeRow = async (row) => {
    setUnmergeConfirm(null)
    setUnmergingRow(row.customer_no)

    try {
      const { ok, status, text } = await callProc(PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(row.keep_cust) },
        { Name: '@customer_no_2', Value: String(row.customer_no) },
        { Name: '@mode',          Value: 'unmerge' }
      ])
      if (ok) {
        setUnmergedRows(prev => new Set([...prev, row.customer_no]))
        setRecentMerges(prev => prev.map(r =>
          r.customer_no === row.customer_no && r.keep_cust === row.keep_cust
            ? { ...r, status: 'U' }
            : r
        ))
        setUnmergeNotice(row)
        await fetchMergeStats()
        await fetchRecentMerges()
      } else {
        setMessage({ type: 'error', text: getErrorMessage('Failed to unmerge.', status, text) })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error unmerging: ' + err.message })
    } finally {
      setUnmergingRow(null)
    }
  }

  const handleUnmergeTool = async () => {
    if (!unmergeKeepNo || !unmergeNo) return
    setToolsLoading('unmerge')
    setUnmergeToolResult(null)
    const keepNo = unmergeKeepNo
    const deletedNo = unmergeNo
    const { ok, text } = await callProc(PROCS.managePair, [
      { Name: '@customer_no_1', Value: String(keepNo) },
      { Name: '@customer_no_2', Value: String(deletedNo) },
      { Name: '@mode',          Value: 'unmerge' }
    ])
    if (ok) {
      setUnmergeToolResult({
        type: 'success',
        keepNo,
        restoredNo: deletedNo,
        ranAt: new Date().toLocaleTimeString()
      })
      setUnmergeKeepNo('')
      setUnmergeNo('')
      await fetchMergeStats()
    } else {
      setUnmergeToolResult({
        type: 'error',
        text: getErrorMessage('Failed to unmerge.', null, text)
      })
    }
    setToolsLoading(null)
  }

  const handleOptimizeIntegrations = async () => {
    setToolsLoading('optimize')
    setOptimizeResult(null)

    const { ok, text, status } = await callProc(PROCS.managePair, [
      { Name: '@mode', Value: 'optimize_integrations_run' }
    ])

    if (!ok) {
      setOptimizeResult({
        type: 'error',
        text: getErrorMessage('Failed to optimize integrations.', status, text)
      })
      setToolsLoading(null)
      return
    }

    let result = {}
    try {
      const rows = JSON.parse(text)
      result = Array.isArray(rows) ? rows[0] || {} : rows || {}
    } catch {
      setOptimizeResult({ type: 'error', text: 'Could not parse optimize result.' })
      setToolsLoading(null)
      return
    }

    const duplicateCount = Number(result.duplicate_login_count || 0)
    setOptimizeResult({
      type: duplicateCount > 0 ? 'warn' : 'success',
      created: Number(result.created_count || 0),
      total: Number(result.candidate_count || 0),
      duplicates: duplicateCount
    })
    setToolsLoading(null)
  }

  const statusLabel = (s) => {
    if (s === 'K') return { label: 'Keep', color: '#38a169' }
    if (s === 'D') return { label: 'Delete', color: '#e53e3e' }
    if (s === 'P') return { label: 'Needs Review', color: '#64748b' }
    return { label: s, color: '#888' }
  }

  const mergeResultLabel = (s) => {
    const status = String(s || '').toUpperCase()
    if (status === 'A') return { label: 'Failed', color: '#dc2626' }
    if (status === 'U') return { label: 'Unmerged', color: '#d97706' }
    if (status === 'M') return { label: 'Merged', color: '#718096' }
    return { label: status || 'Unknown', color: '#718096' }
  }

  const promoted = pool.filter(r => r.status === 'K' || r.status === 'D')
  const canCommit = promoted.length > 0 && !loading
  const allPairs = Object.values(
    pool.reduce((acc, row) => {
      if (!acc[row.criterion]) acc[row.criterion] = []
      acc[row.criterion].push(row)
      return acc
    }, {})
  ).filter(group => group.length === 2)
 
  const activePairs = allPairs
    .filter(group => group.some(r => r.status === 'K' || r.status === 'D'))
    .map(group => group.sort((a, b) => (a.status === 'K' ? -1 : 1)))
 
  const pendingPairs = allPairs
    .filter(group => group.every(r => r.status === 'P') && (showAll || unscheduledCriteria.has(group[0].criterion)))

  const totalPendingPairs = allPairs
    .filter(group => group.every(r => r.status === 'P'))
 
  const pairs = [...activePairs, ...pendingPairs]
  const shouldShowNeedsReviewPrompt = pool.length > 0 && activePairs.length === 0 && totalPendingPairs.length > 0
 
  return (
    <div style={{ ...styles.container, ...(isMobile ? styles.containerMobile : {}) }}>
      <style>{`
        @keyframes crmbuddy-spin { to { transform: rotate(360deg); } }
        @keyframes tessBuddyStatPulse {
          0% { opacity: 0.45; transform: scaleX(0.92); }
          50% { opacity: 1; transform: scaleX(1); }
          100% { opacity: 0.45; transform: scaleX(0.92); }
        }
      `}</style>
      <h2 style={styles.title}>Constituent Merge</h2>
      <p style={styles.subtitle}>
        Identify duplicate constituent records, review them, and commit merges.
      </p>

      {loading && step === 'committing' && (
        <MergeToolLoadingPopup
          title="Committing merges..."
          text="This may take a moment. Please don't close the page."
        />
      )}

      {unmergingRow && (
        <MergeToolLoadingPopup
          title="Restoring record..."
          text="This may take a moment. Please don't close the page."
        />
      )}

      <div style={{ ...styles.tabs, ...(isMobile ? styles.tabsMobile : {}) }}>
        <button
          style={activeTab === 'bulk' ? styles.tabActive : styles.tab}
          onClick={() => { setActiveTab('bulk'); fetchMergeStats() }}
        >
          Bulk Merge
        </button>
        <button
          style={activeTab === 'tools' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('tools')}
        >
          Merge Tools
        </button>
        <button
          style={activeTab === 'recent' ? styles.tabActive : styles.tab}
          onClick={() => { setActiveTab('recent'); fetchRecentMerges() }}
        >
          Recent Merges
        </button>
      </div>

      {activeTab === 'bulk' && (<>

      <div style={{ ...styles.statsGrid, ...(isMobile ? styles.statsGridMobile : {}) }}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Ready to merge</div>
          <div style={styles.statValue}>{statsLoading && !mergeStats ? <LoadingStatValue /> : mergeStats?.activePairs ?? 0}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Needs review</div>
          <div style={styles.statValue}>{statsLoading && !mergeStats ? <LoadingStatValue /> : mergeStats?.needsReviewPairs ?? 0}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Merge history</div>
          <div style={styles.statValue}>{statsLoading && !mergeStats ? <LoadingStatValue /> : mergeStats?.successfulMerges ?? 0}</div>
        </div>
        <div style={{ ...styles.statCard, ...styles.sourceCard }}>
          <div style={styles.statLabel}>Top Duplicate Sources</div>
          {statsLoading && !mergeStats ? (
            <LoadingSourceList />
          ) : mergeStats?.sources?.length > 0 ? (
            <div style={styles.sourceList}>
              {mergeStats.sources.slice(0, 3).map(source => (
                <div key={source.name} style={styles.sourceRow}>
                  <span style={styles.sourceDot} />
                  <span style={styles.sourceLabel}>{source.name}</span>
                  <span style={styles.sourceValue}>{source.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.statHint}>No source history yet</div>
          )}
        </div>
      </div>

      <div style={{ ...styles.actionRow, ...(isMobile ? styles.actionRowMobile : {}) }}>
        <div style={{ ...styles.actionCard, ...styles.stepOneCard, ...(isMobile ? styles.stepCardMobile : {}) }}>
          <h3 style={styles.actionTitle}>Step 1 — Find & Recommend</h3>
          <p style={styles.actionDesc}>
            Scans for duplicate constituents and recommends merge candidates for your review.
          </p>
          <button
            style={styles.button}
            onClick={handleFindAndPromote}
            disabled={loading}
          >
            {loading && step === 'finding' ? 'Processing...' : 'Load Duplicates'}
          </button>
          {pool.length > 0 && (
            <div style={styles.toggleRow}>
              <span style={{ ...styles.toggleLabel, fontWeight: !showAll ? '700' : '400' }}>High Confidence</span>
              <div
                style={{ ...styles.toggle, backgroundColor: showAll ? '#1a1a2e' : '#cbd5e0' }}
                onClick={() => setShowAll(prev => !prev)}
              >
                <div style={{ ...styles.toggleKnob, transform: showAll ? 'translateX(18px)' : 'translateX(2px)' }} />
              </div>
              <span style={{ ...styles.toggleLabel, fontWeight: showAll ? '700' : '400' }}>Needs Review</span>
            </div>
          )}
        </div>
 
        {!isMobile && <div style={styles.stepArrow}>→</div>}
 
        <div style={{ ...styles.actionCard, ...styles.stepTwoCard, ...(isMobile ? styles.stepCardMobile : {}) }}>
          <h3 style={styles.actionTitle}>Step 2 — Review & Commit</h3>
          <p style={styles.actionDesc}>
            Review high confidence pairs below, promote any needs review suggestions, then commit when ready.
          </p>
          <button
            style={{
              ...styles.button,
              ...(canCommit ? styles.commitReadyButton : styles.disabledButton)
            }}
            onClick={() => setConfirmCommit(true)}
            disabled={!canCommit}
          >
            {loading && step === 'committing' ? 'Committing...' : 'Commit Merges'}
          </button>
        </div>
      </div>

      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? '#f0fff4' : '#fff5f5',
          borderColor: message.type === 'success' ? '#9ae6b4' : '#feb2b2'
        }}>
          {message.text}
        </div>
      )}

      {shouldShowNeedsReviewPrompt && (
        <div style={{ ...styles.message, ...styles.infoMessage }}>
          All high confidence merge recommendations are complete. Switch to Needs Review to evaluate the remaining suggestions.
        </div>
      )}
 
      {confirmCommit && (
        <div style={styles.overlay}>
          <div style={styles.dialog}>
            <h3 style={styles.dialogTitle}>Confirm Merge</h3>
            <p style={styles.dialogText}>
              You are about to commit <strong>{activePairs.length} merges</strong>.
              This cannot be undone. Are you sure?
            </p>
            <div style={styles.dialogButtons}>
              <button style={styles.cancelButton} onClick={() => setConfirmCommit(false)}>
                Cancel
              </button>
              <button style={styles.commitButton} onClick={handleCommit}>
                Yes, Commit Merges
              </button>
            </div>
          </div>
        </div>
      )}
 
      {pairs.length > 0 && (
        <div style={styles.tableSection}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              Merge Candidates — {activePairs.length} high confidence{pendingPairs.length > 0 ? `, ${pendingPairs.length} needs review` : ''}
            </h3>
          </div>
          {isMobile ? (
            <div style={styles.mobilePairList}>
              {pairs.map((pair, pairIndex) => {
                const isPending = pair.every(r => r.status === 'P')
                const keepRow = pair.find(r => r.status === 'K')
                const deleteRow = pair.find(r => r.status === 'D')
                const isSwapping = swappingCriterion === pair[0]?.criterion
                const isCancelling = cancellingCriterion === pair[0]?.criterion
                const firstRow = pair[0]
                const isBlocking = blockingCustomer === firstRow?.customer_no

                return (
                  <div key={`${firstRow?.criterion}-${pairIndex}`} style={styles.mobilePairCard}>
                    <div style={styles.mobilePairTop}>
                      <div>
                        <div style={styles.mobileCriterion}>{firstRow?.criterion}</div>
                        <div style={styles.mobileMuted}>{isPending ? 'Needs review' : 'High confidence'}</div>
                      </div>
                    </div>
                    <div style={styles.mobileRecordList}>
                      {pair.map((row, rowIndex) => {
                        const s = statusLabel(row.status)
                        const constituentUrl = `${TESSITURA_BASE}/${row.customer_no}/dashboard`
                        return (
                          <div key={`${row.customer_no}-${rowIndex}`} style={styles.mobileRecord}>
                            <div style={styles.mobileRecordHeader}>
                              <span style={{ ...styles.badge, ...(row.status === 'P' ? styles.needsReviewBadge : {}), backgroundColor: s.color }}>
                                {s.label}
                              </span>
                              <a href={constituentUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                                #{row.customer_no}
                              </a>
                            </div>
                            <div style={styles.mobileRecordName}>{row.fname} {row.lname}</div>
                            <div style={styles.mobileMetaRow}>
                              <span>Associations</span>
                              <strong>{Number(row.association_count || 0)}</strong>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {isPending ? (
                      <div style={styles.mobileActions}>
                        <button
                          style={{ ...styles.blockMergeButton, ...styles.mobileActionButton, opacity: isBlocking ? 0.5 : 1, cursor: isBlocking ? 'not-allowed' : 'pointer' }}
                          onClick={() => handleBlockMerge(firstRow)}
                          disabled={isBlocking}
                        >
                          {isBlocking ? '...' : 'Block Merges'}
                        </button>
                        <button
                          style={{ ...styles.mergeButton, ...styles.mobileActionButton, opacity: isCancelling || convertingCriterion === firstRow.criterion ? 0.5 : 1, cursor: isCancelling || convertingCriterion === firstRow.criterion ? 'not-allowed' : 'pointer' }}
                          onClick={() => handleSchedule(firstRow.criterion, pair[0], pair[1])}
                          disabled={isCancelling || convertingCriterion === firstRow.criterion}
                        >
                          {isCancelling ? '...' : 'Promote'}
                        </button>
                        <button
                          style={{ ...styles.mergeButton, ...styles.mobileActionButton, opacity: isCancelling || convertingCriterion === firstRow.criterion ? 0.5 : 1, cursor: isCancelling || convertingCriterion === firstRow.criterion ? 'not-allowed' : 'pointer' }}
                          onClick={() => handleConvertToHousehold(firstRow.criterion, pair[0], pair[1])}
                          disabled={isCancelling || convertingCriterion === firstRow.criterion}
                        >
                          Create HH
                        </button>
                      </div>
                    ) : keepRow && deleteRow ? (
                      <div style={styles.mobileActions}>
                        <button
                          style={{ ...styles.swapButton, ...styles.mobileActionButton, opacity: isSwapping || isCancelling ? 0.5 : 1, cursor: isSwapping || isCancelling ? 'not-allowed' : 'pointer' }}
                          onClick={() => handleSwap(firstRow.criterion, keepRow, deleteRow)}
                          disabled={isSwapping || isCancelling}
                        >
                          Swap
                        </button>
                        <button
                          style={{ ...styles.cancelPairButton, ...styles.mobileActionButton, opacity: isSwapping || isCancelling ? 0.5 : 1, cursor: isSwapping || isCancelling ? 'not-allowed' : 'pointer' }}
                          onClick={() => handleCancel(firstRow.criterion, keepRow, deleteRow)}
                          disabled={isSwapping || isCancelling}
                        >
                          Remove
                        </button>
                        <button
                          style={{ ...styles.mergeButton, ...styles.mobileActionButton, opacity: isSwapping || isCancelling || convertingCriterion === firstRow.criterion ? 0.5 : 1, cursor: isSwapping || isCancelling || convertingCriterion === firstRow.criterion ? 'not-allowed' : 'pointer' }}
                          onClick={() => handleConvertToHousehold(firstRow.criterion, keepRow, deleteRow)}
                          disabled={isSwapping || isCancelling || convertingCriterion === firstRow.criterion}
                        >
                          Create HH
                        </button>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, ...styles.statusCol }}>Status</th>
                  <th style={{ ...styles.th, ...styles.customerCol }}>Customer #</th>
                  <th style={{ ...styles.th, ...styles.nameCol }}>Name</th>
                  <th style={{ ...styles.th, ...styles.associationsCol }}>Associations</th>
                  <th style={{ ...styles.th, ...styles.criterionCol }}>Criterion</th>
                  <th style={{ ...styles.th, ...styles.actionsCol }}></th>
                </tr>
              </thead>
              <tbody>
                {pairs.map((pair, pairIndex) => {
                  const isPending = pair.every(r => r.status === 'P')
                  const keepRow = pair.find(r => r.status === 'K')
                  const deleteRow = pair.find(r => r.status === 'D')
                  const isSwapping = swappingCriterion === pair[0]?.criterion
                  const isCancelling = cancellingCriterion === pair[0]?.criterion
 
                  return pair.map((row, rowIndex) => {
                    const s = statusLabel(row.status)
                    const isKeepRow = row.status === 'K'
                    const isFirstRow = rowIndex === 0
                    const globalIndex = pairIndex * 2 + rowIndex
                    const constituentUrl = `${TESSITURA_BASE}/${row.customer_no}/dashboard`
                    const rowCellStyle = isPending ? styles.pendingTd : styles.td
                    const isBlocking = blockingCustomer === row.customer_no
 
                    return (
                      <tr
                        key={`${row.customer_no}-${rowIndex}`}
                        style={{
                          ...(globalIndex % 2 === 0 ? styles.rowEven : styles.rowOdd),
                          ...(isPending ? styles.pendingRow : {}),
                          borderTop: rowIndex === 0 && pairIndex > 0 ? '2px solid #e2e8f0' : undefined
                        }}
                      >
                        <td style={{ ...rowCellStyle, ...styles.statusCol }}>
                          <span style={{ ...styles.badge, ...(row.status === 'P' ? styles.needsReviewBadge : {}), backgroundColor: s.color }}>
                            {s.label}
                          </span>
                        </td>
                        <td style={{ ...rowCellStyle, ...styles.customerCol }}>
                          <a
                            href={constituentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.customerLink}
                          >
                            {row.customer_no}
                          </a>
                        </td>
                        <td style={{ ...rowCellStyle, ...styles.nameCol }}>{row.fname} {row.lname}</td>
                        <td style={{ ...rowCellStyle, ...styles.associationsCol }}>{Number(row.association_count || 0)}</td>
                        <td style={{ ...rowCellStyle, ...styles.criterionCol }}>{row.criterion}</td>
                        <td style={{ ...styles.td, ...styles.actionsCol, ...styles.actionsTd }}>
                          {isPending && isFirstRow && (
                            <div style={styles.actionButtons}>
                              <button
                                style={{
                                  ...styles.blockMergeButton,
                                  opacity: isBlocking ? 0.5 : 1,
                                  cursor: isBlocking ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleBlockMerge(row)}
                                disabled={isBlocking}
                                title="Apply the Block Merges attribute to this customer"
                              >
                                {isBlocking ? '...' : 'Block Merges'}
                              </button>
                              {isFirstRow && (
                                <>
                                  <button
                                    style={{
                                      ...styles.mergeButton,
                                      opacity: isCancelling || convertingCriterion === row.criterion ? 0.5 : 1,
                                      cursor: isCancelling || convertingCriterion === row.criterion ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => handleSchedule(row.criterion, pair[0], pair[1])}
                                    disabled={isCancelling || convertingCriterion === row.criterion}
                                    title="Promote this needs review pair to high confidence"
                                  >
                                    {isCancelling ? '...' : 'Promote'}
                                  </button>
                                  <button
                                    style={{
                                      ...styles.mergeButton,
                                      fontSize: '0.7rem',
                                      opacity: isCancelling || convertingCriterion === row.criterion ? 0.5 : 1,
                                      cursor: isCancelling || convertingCriterion === row.criterion ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => handleConvertToHousehold(row.criterion, pair[0], pair[1])}
                                    disabled={isCancelling || convertingCriterion === row.criterion}
                                    title="Convert this pair into a household"
                                  >
                                    {convertingCriterion === row.criterion ? '...' : '🏠 Create HH'}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {!isPending && isKeepRow && keepRow && deleteRow && (
                            <div style={styles.actionButtons}>
                              <button
                                style={{
                                  ...styles.swapButton,
                                  opacity: isSwapping || isCancelling ? 0.5 : 1,
                                  cursor: isSwapping || isCancelling ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleSwap(row.criterion, keepRow, deleteRow)}
                                disabled={isSwapping || isCancelling}
                                title="Swap Keep / Delete"
                              >
                                {isSwapping ? '...' : '⇄ Swap'}
                              </button>
                              <button
                                style={{
                                  ...styles.cancelPairButton,
                                  opacity: isSwapping || isCancelling ? 0.5 : 1,
                                  cursor: isSwapping || isCancelling ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleCancel(row.criterion, keepRow, deleteRow)}
                                disabled={isSwapping || isCancelling}
                                title="Remove from merge queue"
                              >
                                {isCancelling ? '...' : '✕ Remove'}
                              </button>
                              <button
                                style={{
                                  ...styles.mergeButton,
                                  fontSize: '0.7rem',
                                  opacity: isSwapping || isCancelling || convertingCriterion === row.criterion ? 0.5 : 1,
                                  cursor: isSwapping || isCancelling || convertingCriterion === row.criterion ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => handleConvertToHousehold(row.criterion, keepRow, deleteRow)}
                                disabled={isSwapping || isCancelling || convertingCriterion === row.criterion}
                                title="Convert this pair into a household"
                              >
                                {convertingCriterion === row.criterion ? '...' : '🏠 Create HH'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
 
      {mergedToday.length > 0 && (
        <div style={styles.tableSection}>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              Committed Merges From Last {RECENT_MERGE_WINDOW_MINUTES} Minutes — {mergedToday.length} records
            </h3>
          </div>
          {isMobile ? (
            <div style={styles.mobilePairList}>
              {mergedToday.map((row, index) => {
                const deletedUrl = `${TESSITURA_BASE}/${row.customer_no}/dashboard`
                const keptUrl = `${TESSITURA_BASE}/${row.keep_cust}/dashboard`
                const resultStatus = mergeResultLabel(row.status)
                const mergeStatus = String(row.status || '').toUpperCase()
                const isUnmerged = unmergedRows.has(row.customer_no) || mergeStatus === 'U'
                const isUnmerging = unmergingRow === row.customer_no
                const canUnmerge = !isUnmerged && mergeStatus === 'M'
                return (
                  <div key={`${row.customer_no}-${index}`} style={styles.mobilePairCard}>
                    <div style={styles.mobileRecordHeader}>
                      <span style={{ ...styles.badge, backgroundColor: resultStatus.color }}>{resultStatus.label}</span>
                      <a href={deletedUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>#{row.customer_no}</a>
                    </div>
                    <div style={styles.mobileRecordName}>{row.fname} {row.lname}</div>
                    <div style={styles.mobileMetaRow}>
                      <span>Kept as</span>
                      <a href={keptUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>#{row.keep_cust}</a>
                    </div>
                    <div style={styles.mobileMetaRow}>
                      <span>Criterion</span>
                      <strong>{row.criterion}</strong>
                    </div>
                    {canUnmerge && (
                      <div style={styles.mobileActions}>
                        <button
                          style={{ ...styles.unmergeButton, ...styles.mobileActionButton, opacity: isUnmerging ? 0.5 : 1, cursor: isUnmerging ? 'not-allowed' : 'pointer' }}
                          onClick={() => setUnmergeConfirm(row)}
                          disabled={isUnmerging}
                        >
                          {isUnmerging ? '...' : 'Unmerge'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Deleted #</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Kept As</th>
                  <th style={styles.th}>Criterion</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {mergedToday.map((row, index) => {
                  const deletedUrl = `${TESSITURA_BASE}/${row.customer_no}/dashboard`
                  const keptUrl = `${TESSITURA_BASE}/${row.keep_cust}/dashboard`
                  const resultStatus = mergeResultLabel(row.status)
                  const mergeStatus = String(row.status || '').toUpperCase()
                  const isUnmerged = unmergedRows.has(row.customer_no) || mergeStatus === 'U'
                  const isUnmerging = unmergingRow === row.customer_no
                  const canUnmerge = !isUnmerged && mergeStatus === 'M'
                  return (
                    <tr
                      key={`${row.customer_no}-${index}`}
                      style={index % 2 === 0 ? styles.rowEven : styles.rowOdd}
                    >
                      <td style={styles.td}>
                        <span
                          style={{ ...styles.badge, backgroundColor: resultStatus.color }}
                          title={String(row.status || '').toUpperCase() === 'A' && row.failure_reason ? row.failure_reason : undefined}
                        >
                          {resultStatus.label}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <a href={deletedUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                          {row.customer_no}
                        </a>
                      </td>
                      <td style={styles.td}>{row.fname} {row.lname}</td>
                      <td style={styles.td}>
                        <a href={keptUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                          {row.keep_cust}
                        </a>
                      </td>
                      <td style={styles.td}>{row.criterion}</td>
                      <td style={{ ...styles.td, ...styles.reviewActionTd }}>
                        {canUnmerge && (
                          <button
                            style={{ ...styles.unmergeButton, opacity: isUnmerging ? 0.5 : 1, cursor: isUnmerging ? 'not-allowed' : 'pointer' }}
                            onClick={() => setUnmergeConfirm(row)}
                            disabled={isUnmerging}
                          >
                            {isUnmerging ? '...' : 'Unmerge'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {convertingCriterion && (
        <MergeToolLoadingPopup title="Creating household..." text="This may take a moment. Please don't close the page." />
      )}

      {householdResult && (
        <div style={styles.overlay} onClick={() => setHouseholdResult(null)}>
          <div style={styles.dialog} onClick={e => e.stopPropagation()}>
            <div style={styles.dialogTitle}>Household Created</div>
            <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8', marginBottom: '20px' }}>
              <strong>{householdResult.name}</strong> (#{householdResult.householdNo})<br />
              A1: {householdResult.a1.fname} {householdResult.a1.lname} (#{householdResult.a1.customer_no})<br />
              A2: {householdResult.a2.fname} {householdResult.a2.lname} (#{householdResult.a2.customer_no})
            </div>
            <div style={{ fontSize: '13px', color: '#555', marginBottom: '20px', lineHeight: '1.6', backgroundColor: '#f7fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              Please review the new household record and make any quick updates needed (e.g. salutation, address, phone preferences).
            </div>
            <div style={styles.dialogButtons}>
              <button style={styles.cancelButton} onClick={() => setHouseholdResult(null)}>Close</button>
              <a
                href={`${TESSITURA_BASE}/${householdResult.householdNo}/dashboard`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...styles.commitButton, textDecoration: 'none', backgroundColor: '#2b6cb0', textAlign: 'center' }}
              >
                View in Tessitura
              </a>
            </div>
          </div>
        </div>
      )}
      </>)}

      {activeTab === 'tools' && (
        <div style={{ ...styles.toolsGrid, ...(isMobile ? styles.toolsGridMobile : {}) }}>
          {toolsLoading && (
            <MergeToolLoadingPopup
              title={getMergeToolLoadingTitle(toolsLoading)}
              text="This may take a moment. Please don't close the page."
            />
          )}
          {oneOffConfirm && (
            <div style={styles.overlay}>
              <div style={styles.dialog}>
                <h3 style={styles.dialogTitle}>Confirm One-off Merge</h3>
                <p style={styles.dialogText}>
                  This will merge <strong>#{oneOffConfirm.deleteCustomer.customerNo} ({oneOffConfirm.deleteCustomer.name})</strong> into <strong>#{oneOffConfirm.keep.customerNo} ({oneOffConfirm.keep.name})</strong>. Continue?
                </p>
                <div style={styles.dialogButtons}>
                  <button style={styles.cancelButton} onClick={() => setOneOffConfirm(null)} disabled={toolsLoading === 'merge'}>
                    Cancel
                  </button>
                  <button style={styles.commitButton} onClick={commitOneOffMerge} disabled={toolsLoading === 'merge'}>
                    Yes, Merge These Records
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ── One-off Merge ── */}
          <div style={styles.toolCard}>
            <div style={styles.toolCardBody}>
              <h3 style={styles.actionTitle}>One-off Merge</h3>
              <p style={styles.actionDesc}>
                Merge a single constituent pair immediately. The delete record's history
                will be rolled into the keep record, then the duplicate is cleared.
              </p>
            </div>
            <div style={styles.toolControlArea}>
              <div style={styles.toolInputRow}>
                <input
                  type="text"
                  placeholder="Keep Customer #"
                  value={mergeKeepNo}
                  onChange={e => { setMergeKeepNo(e.target.value); setMergeToolResult(null); setOneOffConfirm(null) }}
                  style={styles.toolInput}
                  disabled={toolsLoading === 'merge'}
                />
                <input
                  type="text"
                  placeholder="Delete Customer #"
                  value={mergeDeleteNo}
                  onChange={e => { setMergeDeleteNo(e.target.value); setMergeToolResult(null); setOneOffConfirm(null) }}
                  style={styles.toolInput}
                  disabled={toolsLoading === 'merge'}
                />
                <button
                  style={{ ...styles.button, ...styles.toolActionButton, opacity: (!mergeKeepNo || !mergeDeleteNo || toolsLoading === 'merge') ? 0.5 : 1, cursor: (!mergeKeepNo || !mergeDeleteNo || toolsLoading === 'merge') ? 'not-allowed' : 'pointer' }}
                  onClick={handleOneOffMerge}
                  disabled={!mergeKeepNo || !mergeDeleteNo || toolsLoading === 'merge'}
                >
                  {toolsLoading === 'merge' ? 'Loading...' : 'Preview Merge'}
                </button>
              </div>
            </div>
            {mergeToolResult && (
              <div style={{
                ...styles.toolResult,
                backgroundColor: mergeToolResult.type === 'error' ? '#fff5f5' : '#f8fafd',
                borderColor: mergeToolResult.type === 'error' ? '#feb2b2' : 'rgba(29,111,219,0.12)'
              }}>
                <span style={{ ...styles.badge, backgroundColor: mergeToolResult.type === 'error' ? '#dc2626' : '#718096', marginRight: '10px' }}>
                  {mergeToolResult.type === 'error' ? 'Error' : 'Merged'}
                </span>
                <span style={styles.toolResultText}>
                  {mergeToolResult.type === 'error'
                    ? mergeToolResult.text
                    : `#${mergeToolResult.deleteNo} (${mergeToolResult.deleteName}) merged into #${mergeToolResult.keepNo} (${mergeToolResult.keepName}) at ${mergeToolResult.mergedAt}`}
                </span>
              </div>
            )}
          </div>

          {/* ── Optimize Integrations ── */}
          <div style={styles.toolCard}>
            <div style={styles.toolCardBody}>
              <h3 style={styles.actionTitle}>Optimize Integrations</h3>
              <p style={styles.optimizeDesc}>
                Create web logins for constituents who have a primary email but no login row.
              </p>
            </div>
            <div style={styles.toolControlArea}>
              <button
                style={{
                  ...styles.button,
                  opacity: toolsLoading === 'optimize' ? 0.5 : 1,
                  cursor: toolsLoading === 'optimize' ? 'not-allowed' : 'pointer'
                }}
                onClick={handleOptimizeIntegrations}
                disabled={toolsLoading === 'optimize'}
              >
                {toolsLoading === 'optimize' ? 'Optimizing...' : 'Optimize'}
              </button>
            </div>
            {optimizeResult && (
              <div style={{
                ...styles.toolResult,
                backgroundColor: optimizeResult.type === 'error' ? '#fff5f5' : optimizeResult.type === 'warn' ? '#fffbeb' : '#f8fafd',
                borderColor: optimizeResult.type === 'error' ? '#feb2b2' : optimizeResult.type === 'warn' ? '#fde68a' : 'rgba(29,111,219,0.12)'
              }}>
                <span style={{
                  ...styles.badge,
                  backgroundColor: optimizeResult.type === 'error' ? '#dc2626' : optimizeResult.type === 'warn' ? '#d97706' : '#16a34a',
                  marginRight: '10px'
                }}>
                  {optimizeResult.type === 'error' ? 'Error' : 'Done'}
                </span>
                <span style={styles.toolResultText}>
                  {optimizeResult.type === 'error'
                    ? optimizeResult.text
                    : `${optimizeResult.created} of ${optimizeResult.total} login${optimizeResult.total === 1 ? '' : 's'} created${optimizeResult.duplicates ? `, ${optimizeResult.duplicates} duplicate username${optimizeResult.duplicates === 1 ? '' : 's'} skipped` : ''}.`}
                </span>
              </div>
            )}
          </div>

          {/* ── Unmerge ── */}
          <div style={styles.toolCard}>
            <div style={styles.toolCardBody}>
              <h3 style={styles.actionTitle}>Unmerge</h3>
              <p style={styles.actionDesc}>
                Reverse a previous merge by restoring a deleted constituent record as its own independent entry.
              </p>
            </div>
            <div style={styles.toolControlArea}>
              <div style={styles.toolInputRow}>
                <input
                  type="text"
                  placeholder="Keep Customer #"
                  value={unmergeKeepNo}
                  onChange={e => { setUnmergeKeepNo(e.target.value); setUnmergeToolResult(null) }}
                  style={styles.toolInput}
                  disabled={toolsLoading === 'unmerge'}
                />
                <input
                  type="text"
                  placeholder="Deleted Customer #"
                  value={unmergeNo}
                  onChange={e => { setUnmergeNo(e.target.value); setUnmergeToolResult(null) }}
                  style={styles.toolInput}
                  disabled={toolsLoading === 'unmerge'}
                />
                <button
                  style={{ ...styles.button, ...styles.toolActionButton, background: 'linear-gradient(135deg, #d97706, #f59e0b)', opacity: (!unmergeKeepNo || !unmergeNo || toolsLoading === 'unmerge') ? 0.5 : 1, cursor: (!unmergeKeepNo || !unmergeNo || toolsLoading === 'unmerge') ? 'not-allowed' : 'pointer' }}
                  onClick={handleUnmergeTool}
                  disabled={!unmergeKeepNo || !unmergeNo || toolsLoading === 'unmerge'}
                >
                  {toolsLoading === 'unmerge' ? 'Restoring...' : 'Unmerge'}
                </button>
              </div>
            </div>
            {unmergeToolResult && (
              <div style={{
                ...styles.toolResult,
                backgroundColor: unmergeToolResult.type === 'error' ? '#fff5f5' : '#f8fafd',
                borderColor: unmergeToolResult.type === 'error' ? '#feb2b2' : 'rgba(29,111,219,0.12)'
              }}>
                <span style={{ ...styles.badge, backgroundColor: unmergeToolResult.type === 'error' ? '#dc2626' : '#d97706', marginRight: '10px' }}>
                  {unmergeToolResult.type === 'error' ? 'Error' : 'Restored'}
                </span>
                <span style={styles.toolResultText}>
                  {unmergeToolResult.type === 'error'
                    ? unmergeToolResult.text
                    : `#${unmergeToolResult.restoredNo} restored from #${unmergeToolResult.keepNo} at ${unmergeToolResult.ranAt}`}
                </span>
              </div>
            )}
          </div>

          {/* ── Schedule Auto-Merge ── */}
          <ScheduleAutoMergeTool orgData={orgData} toolCardMode />

        </div>
      )}

      {activeTab === 'recent' && (
        <>
          <div style={styles.tableHeader}>
            <h3 style={styles.tableTitle}>
              Recent Merges {recentMerges.length > 0 && `— ${recentMerges.length} merge${recentMerges.length === 1 ? '' : 's'}`}
            </h3>
          </div>

          {recentLoading && (
            <div style={styles.emptyState}>Loading...</div>
          )}

          {!recentLoading && recentMerges.length === 0 && (
            <div style={styles.emptyState}>No merge history on record.</div>
          )}

          {recentMerges.length > 0 && (isMobile ? (
            <div style={styles.mobilePairList}>
              {recentMerges.map((row, index) => {
                const deletedUrl = `${TESSITURA_BASE}/${row.customer_no}/dashboard`
                const keptUrl = `${TESSITURA_BASE}/${row.keep_cust}/dashboard`
                const isUnmerged = unmergedRows.has(row.customer_no)
                const isUnmerging = unmergingRow === row.customer_no
                const mergeStatus = String(row.status || '').toUpperCase()
                const failureReason = String(row.failure_reason || '').trim()
                const statusBadge = isUnmerged
                  ? { label: 'Unmerged', color: '#d97706' }
                  : mergeStatus === 'U'
                    ? { label: 'Unmerged', color: '#d97706' }
                    : mergeStatus === 'M'
                      ? { label: 'Merged', color: '#16a34a' }
                      : mergeStatus === 'A'
                        ? { label: 'Failed', color: '#dc2626' }
                        : { label: mergeStatus || 'Unknown', color: '#718096' }
                const canUnmerge = !isUnmerged && mergeStatus === 'M'
                return (
                  <div key={`${row.customer_no}-${index}`} style={styles.mobilePairCard}>
                    <div style={styles.mobileRecordHeader}>
                      <span style={{ ...styles.badge, backgroundColor: statusBadge.color }}>{statusBadge.label}</span>
                      <a href={deletedUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>#{row.customer_no}</a>
                    </div>
                    <div style={styles.mobileRecordName}>{row.fname} {row.lname}</div>
                    <div style={styles.mobileMetaRow}>
                      <span>Kept as</span>
                      <a href={keptUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>#{row.keep_cust}</a>
                    </div>
                    <div style={styles.mobileMetaRow}>
                      <span>Criterion</span>
                      <strong>{row.criterion}</strong>
                    </div>
                    {mergeStatus === 'A' && failureReason && (
                      <div style={styles.mobileFailureReason}>{failureReason}</div>
                    )}
                    {canUnmerge && (
                      <div style={styles.mobileActions}>
                        <button
                          style={{ ...styles.unmergeButton, ...styles.mobileActionButton, opacity: isUnmerging ? 0.5 : 1, cursor: isUnmerging ? 'not-allowed' : 'pointer' }}
                          onClick={() => setUnmergeConfirm(row)}
                          disabled={isUnmerging}
                        >
                          {isUnmerging ? '...' : 'Unmerge'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Deleted #</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Kept As</th>
                    <th style={styles.th}>Criterion</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {recentMerges.map((row, index) => {
                    const deletedUrl = `${TESSITURA_BASE}/${row.customer_no}/dashboard`
                    const keptUrl = `${TESSITURA_BASE}/${row.keep_cust}/dashboard`
                    const isUnmerged = unmergedRows.has(row.customer_no)
                    const isUnmerging = unmergingRow === row.customer_no
                    const mergeStatus = String(row.status || '').toUpperCase()
                    const failureReason = String(row.failure_reason || '').trim()
                    const failureKey = `${row.customer_no}-${row.keep_cust}-${index}`
                      const statusBadge = isUnmerged
                        ? { label: 'Unmerged', color: '#d97706' }
                        : mergeStatus === 'U'
                          ? { label: 'Unmerged', color: '#d97706' }
                      : mergeStatus === 'M'
                        ? { label: 'Merged', color: '#16a34a' }
                        : mergeStatus === 'A'
                          ? { label: 'Failed', color: '#dc2626' }
                          : { label: mergeStatus || 'Unknown', color: '#718096' }
                    const canUnmerge = !isUnmerged && mergeStatus === 'M'
                    return (
                      <tr
                        key={`${row.customer_no}-${index}`}
                        style={index % 2 === 0 ? styles.rowEven : styles.rowOdd}
                      >
                        <td style={{ ...styles.td, position: 'relative' }}>
                          <span
                            style={{ ...styles.badge, backgroundColor: statusBadge.color }}
                            title={mergeStatus === 'A' && failureReason ? failureReason : undefined}
                            onMouseEnter={() => {
                              if (mergeStatus === 'A' && failureReason) setHoveredFailureKey(failureKey)
                            }}
                            onMouseLeave={() => setHoveredFailureKey(null)}
                          >
                            {statusBadge.label}
                          </span>
                          {mergeStatus === 'A' && failureReason && hoveredFailureKey === failureKey && (
                            <div style={styles.failureTooltip}>
                              {failureReason}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <a href={deletedUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                            {row.customer_no}
                          </a>
                        </td>
                        <td style={styles.td}>{row.fname} {row.lname}</td>
                        <td style={styles.td}>
                          <a href={keptUrl} target="_blank" rel="noopener noreferrer" style={styles.customerLink}>
                            {row.keep_cust}
                          </a>
                        </td>
                        <td style={styles.td}>{row.criterion}</td>
                        <td style={styles.td}>
                          {canUnmerge && (
                            <button
                              style={{ ...styles.unmergeButton, opacity: isUnmerging ? 0.5 : 1, cursor: isUnmerging ? 'not-allowed' : 'pointer' }}
                              onClick={() => setUnmergeConfirm(row)}
                              disabled={isUnmerging}
                            >
                              {isUnmerging ? '...' : 'Unmerge'}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      {unmergeConfirm && (
        <div style={styles.overlay}>
          <div style={styles.dialog}>
            <h3 style={styles.dialogTitle}>Confirm Unmerge</h3>
            <p style={styles.dialogText}>
              This will restore <strong>#{unmergeConfirm.customer_no} ({unmergeConfirm.fname} {unmergeConfirm.lname})</strong> as a separate constituent record, detaching it from <strong>#{unmergeConfirm.keep_cust}</strong>. Continue?
            </p>
            <div style={styles.dialogButtons}>
              <button style={styles.cancelButton} onClick={() => setUnmergeConfirm(null)}>Cancel</button>
              <button style={{ ...styles.commitButton, backgroundColor: '#fef3c7', color: '#d97706', borderColor: '#fde68a' }} onClick={() => handleUnmergeRow(unmergeConfirm)}>
                Yes, Unmerge
              </button>
            </div>
          </div>
        </div>
      )}

      {unmergeNotice && (
        <div style={styles.overlay}>
          <div style={styles.dialog}>
            <h3 style={styles.dialogTitle}>Unmerge Complete</h3>
            <p style={styles.dialogText}>
              <strong>#{unmergeNotice.customer_no} ({unmergeNotice.fname} {unmergeNotice.lname})</strong> was restored as a separate constituent record.
              Memberships were not restored automatically and must be reviewed manually.
            </p>
            <div style={styles.dialogButtons}>
              <button style={styles.commitButton} onClick={() => setUnmergeNotice(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

// ─── Merge tool flat cards (exported for homescreen Quick Tools) ──────────────

const getQuickToolErrorMessage = (fallback, status, text) => {
  const statusText = status ? ` Tessitura returned HTTP ${status}.` : ''
  const noDetails = `${fallback}${statusText} No error details were returned.`
  if (!text) return noDetails
  try {
    const parsed = JSON.parse(text)
    const tessituraDescription = Array.isArray(parsed)
      ? parsed.find(item => typeof item?.Description === 'string')?.Description
      : parsed?.Description
    const primary = parsed?.Message || parsed?.message || parsed?.error
    const retryText = parsed?.retryAttempt?.text?.Message || parsed?.retryAttempt?.text?.message || parsed?.retryAttempt?.text?.error || parsed?.retryAttempt?.text
    const firstText = parsed?.firstAttempt?.text?.Message || parsed?.firstAttempt?.text?.message || parsed?.firstAttempt?.text?.error || parsed?.firstAttempt?.text
    const upstreamText = parsed?.upstreamText || parsed?.text
    const details = [tessituraDescription, retryText, firstText, upstreamText, primary]
      .find(detail => typeof detail === 'string' && detail.trim())
    if (details) return details.length > 280 ? `${details.slice(0, 280)}...` : details
    if (parsed?.requestBody?.ProcedureName) {
      return `${fallback}${statusText} Tessitura rejected ${parsed.requestBody.ProcedureName}, but did not return details.`
    }
    return noDetails
  } catch {
    return text.length > 280 ? `${text.slice(0, 280)}...` : text
  }
}

const callQuickMergeProc = async (orgData, proc, parameterValues = []) => {
  const response = await fetch(`/api/tessitura?endpoint=Custom/Execute`, {
    method: 'POST',
    headers: {
      'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
      'x-tessitura-url': orgData.organizations.tessitura_base_url,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(buildCustomExecuteBody(proc, parameterValues))
  })
  const text = await response.text()
  return { ok: response.ok, status: response.status, text }
}

const callQuickTessitura = async (orgData, endpoint) => {
  const response = await fetch(`/api/tessitura?endpoint=${encodeURIComponent(endpoint)}`, {
    method: 'GET',
    headers: {
      'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
      'x-tessitura-url': orgData.organizations.tessitura_base_url,
      'Content-Type': 'application/json'
    }
  })
  const text = await response.text()
  return { ok: response.ok, status: response.status, text }
}

const fetchQuickConstituentPreview = async (orgData, customerNo) => {
  const id = String(customerNo || '').trim()
  const { ok, status, text } = await callQuickTessitura(orgData, `CRM/Constituents/${encodeURIComponent(id)}`)
  if (!ok) {
    throw new Error(getQuickToolErrorMessage(`Could not load customer #${id}.`, status, text))
  }
  let data = {}
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error(`Could not read customer #${id} from Tessitura.`)
  }
  return { customerNo: id, name: getConstituentName(data, id) }
}

const getMergeToolLoadingTitle = (loadingKey) => {
  if (loadingKey === 'merge') return 'Merging records...'
  if (loadingKey === 'separate') return 'Separating household...'
  if (loadingKey === 'unmerge') return 'Restoring record...'
  if (loadingKey === 'optimize') return 'Optimizing integrations...'
  if (loadingKey === 'schedule') return 'Updating auto-merge schedule...'
  return 'Working...'
}

function MergeToolLoadingPopup({ title, text = "This may take a moment. Please don't close the page." }) {
  return (
    <div style={styles.overlay}>
      <style>{`@keyframes crmbuddy-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.loadingDialog}>
        <div style={styles.spinner} />
        <div style={styles.loadingTitle}>{title}</div>
        <div style={styles.loadingText}>{text}</div>
      </div>
    </div>
  )
}

function OneOffMergeTool({ orgData, onUsageUpdated }) {
  const [keepNo, setKeepNo]     = useState('')
  const [deleteNo, setDeleteNo] = useState('')
  const [loading, setLoading]   = useState(false)
  const [result, setResult]     = useState(null)
  const [confirmMerge, setConfirmMerge] = useState(null)

  const handleMerge = async () => {
    if (!keepNo.trim() || !deleteNo.trim() || !orgData) return
    setLoading(true)
    setResult(null)
    setConfirmMerge(null)

    try {
      const [keep, deleteCustomer] = await Promise.all([
        fetchQuickConstituentPreview(orgData, keepNo),
        fetchQuickConstituentPreview(orgData, deleteNo)
      ])
      setConfirmMerge({ keep, deleteCustomer })
    } catch (err) {
      setResult({ type: 'error', text: err.message })
    }

    setLoading(false)
  }

  const commitConfirmedMerge = async () => {
    if (!confirmMerge || !orgData) return
    const keepCustomerNo = confirmMerge.keep.customerNo
    const deleteCustomerNo = confirmMerge.deleteCustomer.customerNo
    setLoading(true)
    setResult(null)

    try {
      const scheduleResponse = await callQuickMergeProc(orgData, PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(keepCustomerNo) },
        { Name: '@customer_no_2', Value: String(deleteCustomerNo) },
        { Name: '@mode', Value: 'one_off_schedule' }
      ])

      if (!scheduleResponse.ok) {
        setResult({ type: 'error', text: getQuickToolErrorMessage('Failed to stage merge pair.', scheduleResponse.status, scheduleResponse.text) })
        setLoading(false)
        return
      }

      const { ok, status, text } = await callQuickMergeProc(orgData, PROCS.mergeCustomerApi, [
        { Name: '@kept_id_filter', Value: String(keepCustomerNo) },
        { Name: '@delete_id_filter', Value: String(deleteCustomerNo) }
      ])
      if (!ok) {
        await callQuickMergeProc(orgData, PROCS.managePair, [
          { Name: '@customer_no_1', Value: String(keepCustomerNo) },
          { Name: '@customer_no_2', Value: String(deleteCustomerNo) },
          { Name: '@mode', Value: 'api_merge_failed' },
          { Name: '@failure_reason', Value: getQuickToolErrorMessage('Tessitura API merge request failed.', status, text) }
        ])
        const { ok: recentOk, text: recentText } = await callQuickMergeProc(orgData, PROCS.managePair, [
          { Name: '@mode', Value: 'merged_recent' }
        ])
        let verifiedRows = []
        if (recentOk) {
          try {
            const parsed = JSON.parse(recentText)
            verifiedRows = Array.isArray(parsed) ? parsed : []
          } catch {
            verifiedRows = []
          }
        }
        const verified = status === 400 && verifiedRows.some(row => String(row.customer_no) === String(deleteCustomerNo) || String(row.keep_cust) === String(keepCustomerNo))
        if (!verified) {
          setResult({ type: 'error', text: getQuickToolErrorMessage('Failed to merge.', status, text) })
          setLoading(false)
          return
        }
      }

      try {
        await addUsageCredits(orgData, CREDIT_PRICES.merge)
        if (onUsageUpdated) await onUsageUpdated()
      } catch (err) {
        console.error('Usage update failed:', err)
      }

      setResult({
        type: 'success',
        keepNo: keepCustomerNo,
        keepName: confirmMerge.keep.name,
        deleteNo: deleteCustomerNo,
        deleteName: confirmMerge.deleteCustomer.name,
        at: new Date().toLocaleTimeString()
      })
      setConfirmMerge(null)
      setKeepNo('')
      setDeleteNo('')
    } catch (err) {
      setResult({ type: 'error', text: 'Error merging: ' + err.message })
    }

    setLoading(false)
  }

  const can = keepNo.trim() && deleteNo.trim() && orgData

  return (
    <div style={qs.card}>
      {loading && <MergeToolLoadingPopup title="Merging records..." />}
      {confirmMerge && (
        <div style={styles.overlay}>
          <div style={styles.dialog}>
            <h3 style={styles.dialogTitle}>Confirm One-off Merge</h3>
            <p style={styles.dialogText}>
              This will merge <strong>#{confirmMerge.deleteCustomer.customerNo} ({confirmMerge.deleteCustomer.name})</strong> into <strong>#{confirmMerge.keep.customerNo} ({confirmMerge.keep.name})</strong>. Continue?
            </p>
            <div style={styles.dialogButtons}>
              <button style={styles.cancelButton} onClick={() => setConfirmMerge(null)} disabled={loading}>
                Cancel
              </button>
              <button style={styles.commitButton} onClick={commitConfirmedMerge} disabled={loading}>
                Yes, Merge These Records
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ ...qs.head, cursor: 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>🔀</span>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>One-off Merge</p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" }}>Merge a single constituent pair immediately</p>
          </div>
        </div>
      </div>
      <div style={qs.body}>
        <div style={qs.fieldGroup}>
          <label style={qs.label}>Keep Customer #</label>
          <input value={keepNo} onChange={e => { setKeepNo(e.target.value); setResult(null); setConfirmMerge(null) }} placeholder="e.g. 100042" style={qs.input} disabled={loading} />
        </div>
        <div style={{ ...qs.fieldGroup, marginBottom: '14px' }}>
          <label style={qs.label}>Delete Customer #</label>
          <input value={deleteNo} onChange={e => { setDeleteNo(e.target.value); setResult(null); setConfirmMerge(null) }} placeholder="e.g. 100099" style={qs.input} disabled={loading} />
        </div>
        <button onClick={handleMerge} disabled={!can || loading} style={{ ...qs.btn, width: '100%', opacity: can && !loading ? 1 : 0.5, cursor: can && !loading ? 'pointer' : 'not-allowed' }}>
          {loading ? 'Loading...' : 'Preview Merge'}
        </button>
        {result && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: result.type === 'error' ? '#fff5f5' : '#f0fff4', border: `1px solid ${result.type === 'error' ? '#feb2b2' : '#c6f6d5'}`, borderRadius: '7px' }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: result.type === 'error' ? '#dc2626' : '#16a34a', fontFamily: "'Inter', sans-serif" }}>
              {result.type === 'error' ? 'Could not merge' : `Merged at ${result.at}`}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#4b5563', fontFamily: "'Inter', sans-serif" }}>
              {result.type === 'error' ? result.text : `#${result.deleteNo} (${result.deleteName}) merged into #${result.keepNo} (${result.keepName})`}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function SeparateHHTool({ orgData }) {
  const [customerOneNo, setCustomerOneNo] = useState('')
  const [customerTwoNo, setCustomerTwoNo] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult]   = useState(null)

  const handleSeparate = async () => {
    if (!customerOneNo.trim() || !customerTwoNo.trim() || !orgData) return
    setLoading(true)
    setResult(null)

    try {
      const { ok, status, text } = await callQuickMergeProc(orgData, PROCS.managePair, [
        { Name: '@customer_no_1', Value: String(customerOneNo.trim()) },
        { Name: '@customer_no_2', Value: String(customerTwoNo.trim()) },
        { Name: '@mode', Value: 'convert_to_household' }
      ])

      if (!ok) {
        setResult({ type: 'error', text: getQuickToolErrorMessage('Failed to create household.', status, text) })
        setLoading(false)
        return
      }

      let householdNo = null
      try {
        const rows = JSON.parse(text)
        const first = Array.isArray(rows) ? rows[0] || {} : rows || {}
        householdNo = first.household_customer_no
      } catch { /* response parsing is best-effort for display only */ }

      setResult({
        type: 'success',
        customerOneNo: customerOneNo.trim(),
        customerTwoNo: customerTwoNo.trim(),
        householdNo,
        at: new Date().toLocaleTimeString()
      })
      setCustomerOneNo('')
      setCustomerTwoNo('')
    } catch (err) {
      setResult({ type: 'error', text: 'Error creating household: ' + err.message })
    }

    setLoading(false)
  }

  const can = customerOneNo.trim() && customerTwoNo.trim() && orgData

  return (
    <div style={qs.card}>
      {loading && <MergeToolLoadingPopup title="Creating household..." />}
      <div style={{ ...qs.head, cursor: 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>🏠</span>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>Create Household</p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" }}>Convert two constituents into one household</p>
          </div>
        </div>
      </div>
      <div style={qs.body}>
        <div style={qs.fieldGroup}>
          <label style={qs.label}>Customer #1</label>
          <input value={customerOneNo} onChange={e => { setCustomerOneNo(e.target.value); setResult(null) }} placeholder="e.g. 200184" style={qs.input} disabled={loading} />
        </div>
        <div style={{ ...qs.fieldGroup, marginBottom: '14px' }}>
          <label style={qs.label}>Customer #2</label>
          <input value={customerTwoNo} onChange={e => { setCustomerTwoNo(e.target.value); setResult(null) }} placeholder="e.g. 200185" style={qs.input} disabled={loading} />
        </div>
        <button onClick={handleSeparate} disabled={!can || loading} style={{ ...qs.btn, width: '100%', opacity: can && !loading ? 1 : 0.5, cursor: can && !loading ? 'pointer' : 'not-allowed' }}>
          {loading ? 'Creating…' : 'Create Household'}
        </button>
        {result && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: result.type === 'error' ? '#fff5f5' : '#f0fff4', border: `1px solid ${result.type === 'error' ? '#feb2b2' : '#c6f6d5'}`, borderRadius: '7px' }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: result.type === 'error' ? '#dc2626' : '#16a34a', fontFamily: "'Inter', sans-serif" }}>
              {result.type === 'error' ? 'Could not create household' : `Household created at ${result.at}`}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#4b5563', fontFamily: "'Inter', sans-serif" }}>
              {result.type === 'error'
                ? result.text
                : `#${result.householdNo || 'new'} created from #${result.customerOneNo} and #${result.customerTwoNo}`}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function UnmergeTool({ orgData }) {
  const [keepNo, setKeepNo]   = useState('')
  const [custNo, setCustNo]   = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult]   = useState(null)

  const handleUnmerge = async () => {
    if (!keepNo.trim() || !custNo.trim() || !orgData) return
    setLoading(true)
    setResult(null)

    try {
      const response = await fetch(`/api/tessitura?endpoint=Custom/Execute`, {
        method: 'POST',
        headers: {
          'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
          'x-tessitura-url': orgData.organizations.tessitura_base_url,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ProcedureId: PROCS.managePair.id,
          ProcedureName: PROCS.managePair.name,
          ParameterValues: [
            { Name: '@customer_no_1', Value: String(keepNo.trim()) },
            { Name: '@customer_no_2', Value: String(custNo.trim()) },
            { Name: '@mode', Value: 'unmerge' }
          ]
        })
      })
      const text = await response.text()
      if (response.ok) {
        setResult({
          type: 'success',
          keepNo: keepNo.trim(),
          custNo: custNo.trim(),
          at: new Date().toLocaleTimeString()
        })
        setKeepNo('')
        setCustNo('')
      } else {
        setResult({
          type: 'error',
          text: getQuickToolErrorMessage('Failed to unmerge.', response.status, text)
        })
      }
    } catch (err) {
      setResult({ type: 'error', text: 'Error unmerging: ' + err.message })
    }

    setLoading(false)
  }

  const can = keepNo.trim() && custNo.trim() && orgData

  return (
    <div style={qs.card}>
      {loading && <MergeToolLoadingPopup title="Restoring record..." />}
      <div style={{ ...qs.head, cursor: 'default' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>↩️</span>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>Unmerge</p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" }}>Restore a previously merged constituent record</p>
          </div>
        </div>
      </div>
      <div style={qs.body}>
        <div style={qs.fieldGroup}>
          <label style={qs.label}>Kept Customer #</label>
          <input value={keepNo} onChange={e => { setKeepNo(e.target.value); setResult(null) }} placeholder="e.g. 100042" style={qs.input} disabled={loading} />
        </div>
        <div style={{ ...qs.fieldGroup, marginBottom: '14px' }}>
          <label style={qs.label}>Deleted Customer #</label>
          <input value={custNo} onChange={e => { setCustNo(e.target.value); setResult(null) }} placeholder="e.g. 100099" style={qs.input} disabled={loading} />
        </div>
        <button onClick={handleUnmerge} disabled={!can || loading} style={{ ...qs.btn, background: 'linear-gradient(135deg, #d97706, #f59e0b)', width: '100%', opacity: can && !loading ? 1 : 0.5, cursor: can && !loading ? 'pointer' : 'not-allowed' }}>
          {loading ? 'Restoring…' : 'Unmerge'}
        </button>
        {result && (
          <div style={{ marginTop: '10px', padding: '8px 12px', background: result.type === 'error' ? '#fff5f5' : '#fffbeb', border: `1px solid ${result.type === 'error' ? '#feb2b2' : '#fde68a'}`, borderRadius: '7px' }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: result.type === 'error' ? '#dc2626' : '#d97706', fontFamily: "'Inter', sans-serif" }}>
              {result.type === 'error' ? 'Could not unmerge' : `Restored at ${result.at}`}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#4b5563', fontFamily: "'Inter', sans-serif" }}>
              {result.type === 'error' ? result.text : `#${result.custNo} restored from #${result.keepNo}`}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Schedule Auto-Merge tool (exported for homescreen Quick Tools) ───────────

const qs = {
  card:       { background: 'white', borderRadius: '12px', border: '1px solid rgba(29,111,219,0.1)', boxShadow: '0 2px 8px rgba(29,111,219,0.06)', overflow: 'hidden' },
  head:       { padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  body:       { borderTop: '1px solid rgba(29,111,219,0.08)', padding: '14px 18px 18px' },
  fieldGroup: { marginBottom: '12px' },
  label:      { display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '5px', fontFamily: "'Inter', sans-serif" },
  input:      { width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: '1px solid rgba(29,111,219,0.18)', borderRadius: '7px', fontSize: '13px', fontFamily: "'Inter', sans-serif", outline: 'none' },
  btn:        { padding: '9px 16px', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer', whiteSpace: 'nowrap' },
}

const MERGE_DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
const MERGE_DAY_ORDER = MERGE_DAYS.reduce((acc, day, index) => ({ ...acc, [day]: index }), {})
const SQL_AGENT_DAY_BITS = { Sunday: 1, Monday: 2, Tuesday: 4, Wednesday: 8, Thursday: 16, Friday: 32, Saturday: 64 }

const parseScheduleDays = (value) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value.filter(day => MERGE_DAYS.includes(day)).sort((a, b) => MERGE_DAY_ORDER[a] - MERGE_DAY_ORDER[b])
  }
  if (typeof value === 'number') {
    return MERGE_DAYS.filter(day => (value & SQL_AGENT_DAY_BITS[day]) === SQL_AGENT_DAY_BITS[day])
  }
  return String(value)
    .split(',')
    .map(day => day.trim())
    .filter(day => MERGE_DAYS.includes(day))
    .sort((a, b) => MERGE_DAY_ORDER[a] - MERGE_DAY_ORDER[b])
}

const normalizeMergeSchedule = (row = {}) => {
  const values = Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[String(key).toLowerCase()] = value
    return acc
  }, {})
  const enabledValue = values.enabled
  const enabled = enabledValue === true || enabledValue === 1 || enabledValue === '1' || String(enabledValue || '').toUpperCase() === 'Y'
  const days = parseScheduleDays(values.days || values.schedule_days || values.days_csv || values.freq_interval)
  return { enabled: enabled && days.length > 0, days }
}

const formatScheduleDays = (days = []) => {
  const list = parseScheduleDays(days)
  if (list.length === 0) return 'not scheduled'
  if (list.length === 7) return 'every day'
  if (list.length === 1) return `every ${list[0]}`
  return `every ${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`
}

const callMergeScheduleProc = async (orgData, parameterValues) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 20000)
  const headers = {
    'x-tessitura-auth': orgData.organizations.tessitura_auth_string,
    'x-tessitura-url': orgData.organizations.tessitura_base_url,
    'Content-Type': 'application/json'
  }
  const makeBody = (includeId = true) => ({
    ...(includeId ? { ProcedureId: PROCS.managePair.id } : {}),
    ProcedureName: PROCS.managePair.name,
    ParameterValues: parameterValues
  })
  const execute = async (body) => {
    const response = await fetch(`/api/tessitura?endpoint=Custom/Execute`, {
      method: 'POST',
      headers,
      signal: controller.signal,
      body: JSON.stringify(body)
    })
    const text = await response.text()
    return { ok: response.ok, status: response.status, text }
  }

  try {
    const result = await execute(makeBody(true))
    if (!result.ok) {
      return execute(makeBody(false))
    }
    return result
  } finally {
    clearTimeout(timeoutId)
  }
}

function ScheduleAutoMergeTool({ orgData, active, onToggle, toolCardMode = false, fillCard = false }) {
  const [selectedDays, setSelectedDays] = useState([])
  const [scheduled, setScheduled]     = useState(null)
  const [saving, setSaving]           = useState(false)
  const [loading, setLoading]         = useState(false)
  const [result, setResult]           = useState(null)

  const accordionMode = typeof onToggle === 'function'
  const showBody      = accordionMode ? active : true

  const fetchSchedule = async () => {
    if (!orgData?.organizations?.tessitura_auth_string || !orgData?.organizations?.tessitura_base_url) {
      setResult({ type: 'error', text: 'Tessitura connection details are not loaded for this organization.' })
      return
    }
    setLoading(true)
    setResult(null)
    try {
      const { ok, status, text } = await callMergeScheduleProc(orgData, [
        { Name: '@mode', Value: 'auto_merge_schedule_get' }
      ])
      if (ok) {
        try {
          const rows = JSON.parse(text)
          const schedule = normalizeMergeSchedule(Array.isArray(rows) ? rows[0] : rows)
          setScheduled(schedule.enabled ? schedule : null)
          setSelectedDays(schedule.days)
        } catch {
          setScheduled(null)
          setSelectedDays([])
          setResult({ type: 'error', text: `Schedule response was not valid JSON: ${String(text || '').slice(0, 220) || 'empty response'}` })
        }
      } else {
        setScheduled(null)
        setResult({ type: 'error', text: getQuickToolErrorMessage('Failed to load auto-merge schedule.', status, text) })
      }
    } catch (err) {
      setScheduled(null)
      setSelectedDays([])
      const message = err.name === 'AbortError'
        ? 'The Tessitura schedule request timed out after 20 seconds.'
        : err.message
      setResult({ type: 'error', text: `Failed to load auto-merge schedule. ${message}` })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSchedule()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgData])

  const toggleDay = (day) => {
    setResult(null)
    setSelectedDays(prev => (
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => MERGE_DAY_ORDER[a] - MERGE_DAY_ORDER[b])
    ))
  }

  const handleSave = async () => {
    if (selectedDays.length === 0) return
    if (!orgData?.organizations?.tessitura_auth_string || !orgData?.organizations?.tessitura_base_url) {
      setResult({ type: 'error', text: 'Tessitura connection details are not loaded for this organization.' })
      return
    }
    setSaving(true)
    setResult(null)
    try {
      const { ok, status, text } = await callMergeScheduleProc(orgData, [
        { Name: '@mode', Value: 'auto_merge_schedule_set' },
        { Name: '@schedule_days', Value: selectedDays.join(',') }
      ])
      if (ok) {
        await fetchSchedule()
        setResult({ type: 'success', text: `Auto-merge schedule updated for ${formatScheduleDays(selectedDays)}.` })
      } else {
        setResult({ type: 'error', text: getQuickToolErrorMessage('Failed to update auto-merge schedule.', status, text) })
      }
    } catch (err) {
      const message = err.name === 'AbortError'
        ? 'The Tessitura schedule request timed out after 20 seconds.'
        : err.message
      setResult({ type: 'error', text: `Failed to update auto-merge schedule. ${message}` })
    } finally {
      setSaving(false)
    }
  }

  const handleCancelSchedule = async () => {
    if (!orgData?.organizations?.tessitura_auth_string || !orgData?.organizations?.tessitura_base_url) {
      setResult({ type: 'error', text: 'Tessitura connection details are not loaded for this organization.' })
      return
    }
    setSaving(true)
    setResult(null)
    try {
      const { ok, status, text } = await callMergeScheduleProc(orgData, [
        { Name: '@mode', Value: 'auto_merge_schedule_disable' }
      ])
      if (ok) {
        setScheduled(null)
        setSelectedDays([])
        setResult({ type: 'success', text: 'Auto-merge schedule disabled.' })
      } else {
        setResult({ type: 'error', text: getQuickToolErrorMessage('Failed to disable auto-merge schedule.', status, text) })
      }
    } catch (err) {
      const message = err.name === 'AbortError'
        ? 'The Tessitura schedule request timed out after 20 seconds.'
        : err.message
      setResult({ type: 'error', text: `Failed to disable auto-merge schedule. ${message}` })
    } finally {
      setSaving(false)
    }
  }

  if (toolCardMode) {
    return (
      <div style={{ ...styles.toolCard, ...(fillCard ? styles.toolCardFill : {}) }}>
        {saving && <MergeToolLoadingPopup title="Updating auto-merge schedule..." />}
        <div style={styles.toolCardBody}>
          <h3 style={styles.actionTitle}>Schedule Auto-Merge</h3>
          <p style={styles.actionDesc}>
            Auto-run bulk merge on a chosen day each week.
          </p>
          {scheduled && (
            <div style={styles.scheduleNotice}>
              Runs <strong>{formatScheduleDays(scheduled.days)}</strong>.
            </div>
          )}
          {loading && !scheduled && (
            <div style={styles.scheduleNotice}>
              Loading schedule...
            </div>
          )}
        </div>
        <div style={styles.toolControlArea}>
          <div style={styles.scheduleDayGroup}>
            <label style={styles.scheduleLabel}>Days of week</label>
            <div style={styles.scheduleDayRow}>
              {MERGE_DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  style={{
                    ...styles.scheduleDayButton,
                    ...(selectedDays.includes(day) ? styles.scheduleDayButtonActive : {})
                  }}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
          <div style={styles.scheduleActionRow}>
            {scheduled && (
              <button
                onClick={handleCancelSchedule}
                disabled={saving}
                style={{ ...styles.button, ...styles.scheduleCancelButton, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Disabling...' : 'Disable'}
              </button>
            )}
              <button
                onClick={handleSave}
                disabled={saving || selectedDays.length === 0}
                style={{ ...styles.button, opacity: saving || selectedDays.length === 0 ? 0.6 : 1, cursor: saving || selectedDays.length === 0 ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
          </div>
        </div>
        {result && (
          <div style={{ ...styles.toolResult, backgroundColor: result.type === 'error' ? '#fff5f5' : '#f8fafd', borderColor: result.type === 'error' ? '#feb2b2' : 'rgba(29,111,219,0.12)' }}>
            <span style={{ ...styles.badge, backgroundColor: result.type === 'error' ? '#dc2626' : '#16a34a', marginRight: '10px' }}>
              {result.type === 'error' ? 'Error' : 'Saved'}
            </span>
            <span style={styles.toolResultText}>{result.text}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={qs.card}>
      {saving && <MergeToolLoadingPopup title="Updating auto-merge schedule..." />}
      <div
        style={{ ...qs.head, cursor: accordionMode ? 'pointer' : 'default' }}
        onClick={accordionMode ? onToggle : undefined}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px', lineHeight: 1 }}>🗓️</span>
          <div>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#0c1a33', margin: 0, fontFamily: "'Space Grotesk', sans-serif" }}>Schedule Auto-Merge</p>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0', fontFamily: "'Inter', sans-serif" }}>
              {scheduled ? formatScheduleDays(scheduled.days) : 'Auto-run bulk merge on selected weekdays'}
            </p>
          </div>
        </div>
        {accordionMode && <span style={{ fontSize: '12px', color: '#9ca3af' }}>{active ? '▲' : '▼'}</span>}
      </div>

      {showBody && (
        <div style={qs.body}>
          {scheduled ? (
            <>
              <div style={{ background: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px' }}>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#16a34a', fontFamily: "'Inter', sans-serif" }}>✓ Auto-merge scheduled</p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#4b5563', fontFamily: "'Inter', sans-serif" }}>
                  Runs <strong>{formatScheduleDays(scheduled.days)}</strong> in the background. Results appear in Recent Merges.
                </p>
              </div>
            </>
          ) : null}
          <div style={{ ...qs.fieldGroup, marginBottom: '14px' }}>
            <label style={qs.label}>Days of week</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {MERGE_DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  style={{
                    padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                    fontFamily: "'Inter', sans-serif", cursor: 'pointer',
                    background: selectedDays.includes(day) ? '#1d6fdb' : '#f0f7ff',
                    color: selectedDays.includes(day) ? 'white' : '#4b5563',
                    border: selectedDays.includes(day) ? 'none' : '1px solid rgba(29,111,219,0.15)',
                  }}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {scheduled && (
              <button
                onClick={handleCancelSchedule}
                disabled={saving}
                style={{ ...qs.btn, background: '#fee2e2', color: '#dc2626', flex: 1, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Disabling…' : 'Disable'}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || selectedDays.length === 0}
              style={{ ...qs.btn, flex: 1, opacity: saving || selectedDays.length === 0 ? 0.6 : 1, cursor: saving || selectedDays.length === 0 ? 'not-allowed' : 'pointer' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          {loading && !scheduled && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#4b5563', fontFamily: "'Inter', sans-serif" }}>Loading schedule...</div>
          )}
          {result && (
            <div style={{ marginTop: '10px', padding: '8px 12px', background: result.type === 'error' ? '#fff5f5' : '#f0fff4', border: `1px solid ${result.type === 'error' ? '#feb2b2' : '#c6f6d5'}`, borderRadius: '7px' }}>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: '600', color: result.type === 'error' ? '#dc2626' : '#16a34a', fontFamily: "'Inter', sans-serif" }}>{result.type === 'error' ? 'Could not save schedule' : 'Schedule updated'}</p>
              <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#4b5563', fontFamily: "'Inter', sans-serif" }}>{result.text}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { padding: '40px' },
  containerMobile: { padding: '20px 14px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#0c1a33', marginBottom: '8px', fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' },
  subtitle: { fontSize: '14px', color: '#4b5563', marginBottom: '24px', fontFamily: "'Inter', sans-serif" },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid rgba(29,111,219,0.15)' },
  tabsMobile: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  tab: { padding: '10px 20px', backgroundColor: 'transparent', color: '#6b7280', border: 'none', borderBottom: '2px solid transparent', fontSize: '14px', fontWeight: '500', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  tabActive: { padding: '10px 20px', backgroundColor: 'transparent', color: '#1d6fdb', border: 'none', borderBottom: '2px solid #1d6fdb', fontSize: '14px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  actionRow: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', columnGap: '32px', rowGap: '24px', marginBottom: '24px', position: 'relative', alignItems: 'stretch' },
  actionRowMobile: { gridTemplateColumns: '1fr', rowGap: '14px' },
  actionCard: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', minWidth: 0, minHeight: '168px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
  stepOneCard: { gridColumn: '1 / span 2' },
  stepTwoCard: { gridColumn: '3 / span 2' },
  stepCardMobile: { gridColumn: '1', minHeight: 'auto', padding: '18px' },
  actionTitle: { fontSize: '15px', fontWeight: '700', color: '#0c1a33', marginBottom: '8px', fontFamily: "'Space Grotesk', sans-serif" },
  actionDesc: { fontSize: '13px', color: '#4b5563', marginBottom: '16px', lineHeight: '1.5', fontFamily: "'Inter', sans-serif" },
  stepArrow: { position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', fontSize: '22px', color: '#9ca3af', pointerEvents: 'none', backgroundColor: '#f0f7ff', padding: '0 4px' },
  button: { width: '100%', padding: '12px', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer', boxShadow: '0 2px 12px rgba(29,111,219,0.25)' },
  commitReadyButton: { background: 'linear-gradient(135deg, #c53030, #ef4444)', cursor: 'pointer', boxShadow: '0 2px 12px rgba(197,48,48,0.22)' },
  disabledButton: { background: '#e5e7eb', color: '#9ca3af', cursor: 'not-allowed', boxShadow: 'none', border: '1px solid #d1d5db' },
  message: { padding: '12px 16px', borderRadius: '8px', border: '1px solid', fontSize: '14px', marginBottom: '24px', fontFamily: "'Inter', sans-serif" },
  infoMessage: { backgroundColor: '#eff6ff', borderColor: '#93c5fd', color: '#1e3a8a' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(12,26,51,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  dialog: { backgroundColor: 'white', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', boxShadow: '0 8px 40px rgba(29,111,219,0.15)', border: '1px solid rgba(29,111,219,0.1)' },
  loadingDialog: { backgroundColor: 'white', borderRadius: '16px', padding: '40px', maxWidth: '360px', width: '90%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', boxShadow: '0 8px 40px rgba(29,111,219,0.15)', border: '1px solid rgba(29,111,219,0.1)' },
  spinner: { width: '40px', height: '40px', border: '4px solid rgba(29,111,219,0.15)', borderTopColor: '#1d6fdb', borderRadius: '50%', animation: 'crmbuddy-spin 0.8s linear infinite' },
  loadingTitle: { fontSize: '16px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" },
  loadingText: { fontSize: '13px', color: '#4b5563', lineHeight: '1.5', fontFamily: "'Inter', sans-serif" },
  dialogTitle: { fontSize: '18px', fontWeight: '700', color: '#0c1a33', marginBottom: '12px', fontFamily: "'Space Grotesk', sans-serif" },
  dialogText: { fontSize: '14px', color: '#4b5563', marginBottom: '24px', lineHeight: '1.6', fontFamily: "'Inter', sans-serif" },
  dialogButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  cancelButton: { padding: '10px 20px', backgroundColor: 'transparent', color: '#6b7280', border: '1px solid rgba(29,111,219,0.2)', borderRadius: '8px', fontSize: '14px', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  commitButton: { padding: '10px 20px', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '14px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  tableSection: { marginTop: '32px' },
  tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
  tableTitle: { fontSize: '16px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" },
  tableWrapper: { overflowX: 'hidden', borderRadius: '12px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)' },
  table: { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: 'white' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid rgba(29,111,219,0.08)', fontFamily: "'Inter', sans-serif" },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151', borderBottom: '1px solid rgba(29,111,219,0.06)', fontFamily: "'Inter', sans-serif" },
  statusCol: { width: '9%' },
  customerCol: { width: '12%' },
  nameCol: { width: '17%' },
  associationsCol: { width: '11%' },
  criterionCol: { width: '17%' },
  actionsCol: { width: '34%' },
  actionsTd: { paddingLeft: '18px', paddingRight: '18px', textAlign: 'right' },
  reviewActionTd: { paddingLeft: '16px', paddingRight: '16px', textAlign: 'center' },
  rowEven: { backgroundColor: 'white' },
  rowOdd: { backgroundColor: '#f8fafd' },
  pendingRow: { backgroundColor: '#f8fafc' },
  pendingTd: { padding: '12px 16px', fontSize: '14px', color: '#94a3b8', borderBottom: '1px solid rgba(29,111,219,0.06)', fontFamily: "'Inter', sans-serif" },
  badge: { padding: '3px 8px', borderRadius: '20px', color: 'white', fontSize: '11px', fontWeight: '700' },
  failureTooltip: { position: 'absolute', top: '34px', left: '16px', zIndex: 20, width: '360px', maxWidth: 'min(360px, calc(100vw - 48px))', padding: '10px 12px', backgroundColor: '#111827', color: 'white', borderRadius: '6px', boxShadow: '0 10px 24px rgba(15,23,42,0.22)', fontSize: '12px', lineHeight: '1.45', fontWeight: '500', whiteSpace: 'normal' },
  needsReviewBadge: { display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: '10px', lineHeight: 1, padding: '5px 7px' },
  customerLink: { color: '#1d6fdb', textDecoration: 'none', fontWeight: '600' },
  actionButtons: { display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'nowrap' },
  swapButton: { width: '116px', boxSizing: 'border-box', height: '30px', padding: '4px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', backgroundColor: 'rgba(29,111,219,0.06)', color: '#1d6fdb', border: '1px solid rgba(29,111,219,0.2)', borderRadius: '6px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  cancelPairButton: { width: '116px', boxSizing: 'border-box', height: '30px', padding: '4px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  blockMergeButton: { width: '116px', boxSizing: 'border-box', height: '30px', padding: '4px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  mergeButton: { width: '116px', boxSizing: 'border-box', height: '30px', padding: '4px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', backgroundColor: '#16a34a', color: 'white', border: '1px solid #15803d', borderRadius: '6px', fontSize: '12px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer', boxShadow: '0 1px 6px rgba(22,163,74,0.18)' },
  unmergeButton: { width: '116px', boxSizing: 'border-box', height: '30px', padding: '4px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap', backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px', fontWeight: '700', fontFamily: "'Inter', sans-serif", cursor: 'pointer' },
  toggleRow: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', justifyContent: 'center' },
  toggleLabel: { fontSize: '12px', color: '#4b5563', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  toggle: { width: '36px', height: '20px', borderRadius: '10px', cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s' },
  toggleKnob: { width: '16px', height: '16px', borderRadius: '50%', backgroundColor: 'white', position: 'absolute', top: '2px', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
  emptyState: { padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '14px', fontFamily: "'Inter', sans-serif" },
  toolsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '24px', alignItems: 'stretch' },
  toolsGridMobile: { gridTemplateColumns: '1fr', gap: '14px' },
  toolCard: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', minHeight: '240px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)', display: 'flex', flexDirection: 'column' },
  toolCardFill: { width: '100%', height: '100%', boxSizing: 'border-box' },
  toolCardBody: { minHeight: '92px' },
  toolControlArea: { marginTop: 'auto' },
  toolCardHeader: { marginBottom: '8px' },
  toolInputRow: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px', alignItems: 'center' },
  toolInput: { width: '100%', boxSizing: 'border-box', minWidth: 0, padding: '10px 12px', border: '1px solid rgba(29,111,219,0.22)', borderRadius: '8px', fontSize: '14px', fontFamily: "'Inter', sans-serif", outline: 'none', color: '#0c1a33' },
  toolActionButton: { gridColumn: '1 / -1', width: '100%', minWidth: 0 },
  toolHint: { fontSize: '11px', color: '#9ca3af', fontStyle: 'italic', marginTop: '10px', fontFamily: "'Inter', sans-serif" },
  toolResult: { display: 'flex', alignItems: 'center', marginTop: '14px', padding: '10px 14px', backgroundColor: '#f8fafd', borderRadius: '8px', border: '1px solid rgba(29,111,219,0.12)' },
  toolResultText: { fontSize: '13px', color: '#374151', fontFamily: "'Inter', sans-serif" },
  optimizeDesc: { fontSize: '13px', color: '#4b5563', lineHeight: '1.5', marginBottom: '10px', fontFamily: "'Inter', sans-serif" },
  scheduleNotice: { display: 'inline-block', fontSize: '12px', color: '#16a34a', backgroundColor: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '7px', padding: '6px 10px', fontFamily: "'Inter', sans-serif" },
  scheduleDayGroup: { marginBottom: '10px' },
  scheduleLabel: { display: 'block', fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '5px', fontFamily: "'Inter', sans-serif" },
  scheduleDayRow: { display: 'flex', flexWrap: 'nowrap', gap: '6px', width: '100%' },
  scheduleActionRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  scheduleDayButton: { flex: '1 1 0', minWidth: 0, padding: '6px 4px', borderRadius: '7px', fontSize: '11px', fontWeight: '600', fontFamily: "'Inter', sans-serif", cursor: 'pointer', background: '#f0f7ff', color: '#4b5563', border: '1px solid rgba(29,111,219,0.15)', textAlign: 'center' },
  scheduleDayButtonActive: { background: '#1d6fdb', color: 'white', border: '1px solid #1d6fdb' },
  scheduleCancelButton: { background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', boxShadow: 'none' },
  optimizeResult: { backgroundColor: '#f8fafd', borderRadius: '8px', border: '1px solid rgba(29,111,219,0.12)', padding: '10px 14px', marginBottom: '4px' },
  optimizeRow: { display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f0f3f8' },
  optimizeKey: { fontSize: '12px', color: '#6b7280', fontFamily: "'Inter', sans-serif" },
  optimizeVal: { fontSize: '12px', fontWeight: '600', color: '#0c1a33', fontFamily: "'Inter', sans-serif" },
  stepsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' },
  step: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  stepNum: { width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #1d6fdb, #38bdf8)', color: 'white', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' },
  stepTitle: { fontSize: '14px', fontWeight: '600', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Inter', sans-serif" },
  stepDesc: { fontSize: '13px', color: '#4b5563', lineHeight: '1.5', fontFamily: "'Inter', sans-serif" },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', flex: 1, minWidth: '180px', boxShadow: '0 2px 16px rgba(29,111,219,0.08)', border: '1px solid rgba(29,111,219,0.1)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' },
  statsGridMobile: { gridTemplateColumns: '1fr', gap: '12px' },
  mobilePairList: { display: 'grid', gridTemplateColumns: '1fr', gap: '12px' },
  mobilePairCard: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid rgba(29,111,219,0.12)', boxShadow: '0 2px 14px rgba(29,111,219,0.08)', padding: '14px', minWidth: 0 },
  mobilePairTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' },
  mobileCriterion: { fontSize: '14px', fontWeight: '800', color: '#0c1a33', fontFamily: "'Space Grotesk', sans-serif" },
  mobileMuted: { fontSize: '12px', color: '#6b7280', fontFamily: "'Inter', sans-serif", marginTop: '2px' },
  mobileRecordList: { display: 'grid', gap: '8px' },
  mobileRecord: { backgroundColor: '#f8fafd', border: '1px solid rgba(29,111,219,0.08)', borderRadius: '8px', padding: '10px' },
  mobileRecordHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' },
  mobileRecordName: { fontSize: '15px', fontWeight: '700', color: '#0c1a33', fontFamily: "'Inter', sans-serif", marginBottom: '8px' },
  mobileMetaRow: { display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12px', color: '#6b7280', fontFamily: "'Inter', sans-serif", paddingTop: '6px', borderTop: '1px solid rgba(29,111,219,0.08)' },
  mobileFailureReason: { marginTop: '10px', padding: '8px 10px', borderRadius: '7px', backgroundColor: '#fff5f5', border: '1px solid #fecaca', color: '#991b1b', fontSize: '12px', lineHeight: '1.45', fontFamily: "'Inter', sans-serif" },
  mobileActions: { display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginTop: '12px' },
  mobileActionButton: { width: '100%', height: '38px' },
  sourceCard: { minWidth: '260px' },
  statLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '8px', fontFamily: "'Inter', sans-serif" },
  statValue: { fontSize: '32px', fontWeight: '700', color: '#0c1a33', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif" },
  loadingStatValue: { display: 'inline-block', width: '72px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, rgba(29,111,219,0.12), rgba(56,189,248,0.28))', transformOrigin: 'left center', animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  statHint: { fontSize: '12px', color: '#9ca3af', marginTop: '8px', fontFamily: "'Inter', sans-serif" },
  sourceList: { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' },
  sourceRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  sourceDot: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0 },
  loadingSourceDot: { width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(29,111,219,0.2)', flexShrink: 0, animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  loadingSourceLine: { height: '14px', width: '70%', borderRadius: '6px', background: 'linear-gradient(135deg, rgba(29,111,219,0.1), rgba(56,189,248,0.22))', animation: 'tessBuddyStatPulse 1.1s ease-in-out infinite' },
  sourceLabel: { fontSize: '13px', color: '#4b5563', flex: 1, fontFamily: "'Inter', sans-serif" },
  sourceValue: { fontSize: '14px', fontWeight: '600', color: '#0c1a33', fontFamily: "'Inter', sans-serif" }
}
 

export { ScheduleAutoMergeTool, OneOffMergeTool, SeparateHHTool, UnmergeTool }
export default ConstituentMerge
